import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Save, Package, Upload, Trash2, ImageIcon, Images, Plus, MoreVertical, Copy,
  Receipt, Scissors, Truck, History, Globe, FileText, ArrowLeft, Sparkles, Crop, Tags, X, Pencil,
  ArrowDownUp, BadgeCheck,
} from 'lucide-react';
import { formatMargem, formatQtd, parseQtd, calcPrecoCartao, formatDataSP, formatDataHoraSP, metaMarkupEfetiva, abaixoDaMeta, motivoEanInvalido } from '@fiodecobre/shared';
import { rotuloMovimentacao, sentidoMovimentacao } from './movimentacao-rotulos';
import { api, ApiError, getToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ComboBox } from '../../components/ComboBox';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { ModalCadastroRapido, type CampoCadastroRapido } from '../../components/ModalCadastroRapido';
import { AutocompleteNCM } from '../../components/AutocompleteNCM';
import { BotaoEstimativaCusto } from '../../components/EstimativaCusto';
import { SecaoFracionamentos } from './SecaoFracionamentos';
import { SecaoCotacoesCatalogo } from './SecaoCotacoesCatalogo';
import { AvaliarFotoCatalogo } from './AvaliarFotoCatalogo';
import { EditorEnquadrarFoto } from './EditorEnquadrarFoto';
import { PickerFotoProduto } from './PickerFotoProduto';
import { Modal } from '../../components/Modal';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { UpperInput } from '../../components/inputs/UpperInput';
import { QuantidadeInput } from '../../components/inputs/QuantidadeInput';
import { LocalizacoesProduto, type LocalProduto } from './LocalizacoesProduto';
import { ModalAjustarEstoque } from './ModalAjustarEstoque';
import { EstoqueEfetivo } from '../../components/EstoqueEfetivo';

// Teto do que TRAFEGA — segue 5 MB, espelhando o multer da rota (upload.routes.ts).
// Exigir Master (2000px+ transparente) nao obriga a subir isso: o editor devolve
// WebP, que carrega alfa e pesa uma fracao do PNG (os 50 masters reais do acervo
// tem mediana 1,1 MB e maximo 2,7 MB em lossless).
const LIMITE_FOTO_MB = 5;
const LIMITE_FOTO_BYTES = LIMITE_FOTO_MB * 1024 * 1024;

// Teto do arquivo ESCOLHIDO, que e outra coisa: ele nao sobe como esta — passa
// pelo editor e volta reencodado em WebP. Um master PNG de 12 MB e legitimo e
// vira ~1 MB antes de sair da maquina. Este numero e so guarda de memoria do
// navegador contra arquivo absurdo, nao limite de upload.
const LIMITE_ORIGEM_BYTES = 60 * 1024 * 1024;

// Foto aplicada na tela da foto e ainda nao salva (mora fora do acervo ate o Salvar).
// Mesmo formato do `ehFotoProvisoria` do servidor (utils/foto-provisoria.ts).
function ehProvisoria(url: string | null | undefined): boolean {
  return !!url && /^\/uploads\/produtos-provisorio\/[a-f0-9]{24}\.webp$/.test(url);
}

// Nome da foto na moldura: o nome que ela tem (ou vai ter) no acervo, sem pasta nem extensao.
function nomeDoArquivo(url: string): string {
  return (url.split('/').pop() ?? '').replace(/\.[a-z0-9]+$/i, '');
}

// Vinculo de fracionamento como FILHO: o produto e abastecido por um pai. Quando presente,
// o estoque deste produto e DERIVADO (proprio + pai x fator) e os campos de estoque ficam
// read-only — ajuste se faz fracionando do pai na aba Fracionamento (DA-261).
interface FracPaiInfo {
  pai_id: number;
  pai_codigo: string | null;
  pai_descricao: string;
  pai_estoque: number;
  fator: number;
  contribuicao: number;
}

interface OpAux { id: number; nome: string }
interface OpUnidade { id: number; sigla: string; nome: string; permite_fracionado?: boolean; ativo?: boolean }
// Unidade inativa (migration 0311) sai da lista de escolha, mas a que o produto JA usa fica —
// marcada "(inativa)" —, senao o combo abre vazio num produto que tem unidade.
function unidadesOfertadas(lista: OpUnidade[], atual: number | string | '') {
  return lista.filter((u) => u.ativo !== false || u.id === atual || u.sigla === atual);
}
const rotuloUnidade = (u: OpUnidade) => `${u.sigla} — ${u.nome}${u.ativo === false ? ' (inativa)' : ''}`;
interface OpSitTrib { id: number; codigo: string; descricao: string; aliquota: string | null }
// `quando` = data formatada no Postgres. O `criado_em` cru sai 3h atrasado quando o JS o
// interpreta (coluna sem fuso guardando hora de Brasilia) — ver GET /produtos/:id/historico.
interface HistoricoLinha { id: number; campo: string; valor_anterior: string | null; valor_novo: string | null; usuario_nome: string | null; criado_em: string; quando: string }

// A corrente do ESTOQUE ([DA-656]) — irma da de cima e de proposito separada: aquela e o que uma
// PESSOA editou no cadastro, esta e o que entrou e saiu (venda, troca, devolucao, nota, ajuste).
// `criado_em` aqui e' `timestamptz` de verdade, entao formata pelo helper da casa — nada de
// `to_char` defensivo, que a [DA-330] aposentou.
interface MovimentacaoLinha {
  id: number;
  tipo: string;
  origem_tipo: string | null;
  origem_id: number | null;
  quantidade: string;
  estoque_antes: string | null;
  estoque_depois: string | null;
  observacao: string | null;
  criado_em: string;
  usuario_login: string | null;
  origem_codigo: string | null;
  destino_codigo: string | null;
  orcamento_numero: string | null;
  nota_numero: string | null;
}
// Previa do "Excluir definitivamente" (GET /produtos/:id/pode-excluir). `impedimentos` vazio =
// o produto nao tem passado nenhum; `cai_junto` e' o cadastro que morre com ele.
interface DiagnosticoExclusao {
  produto: { id: number; codigo_interno: string | null; descricao: string; estoque_atual: string | null; foto_url: string | null };
  impedimentos: Array<{ rotulo: string; qtd: number }>;
  cai_junto: Record<string, number>;
  pode_excluir: boolean;
}
// Nome de tabela -> como o operador chama aquilo.
const ROTULO_CAI_JUNTO: Record<string, string> = {
  produto_localizacoes: 'localizacao(oes)',
  produto_marcas_compra: 'marca(s) de compra',
  produto_fornecedor_vinculos: 'vinculo(s) de catalogo do fornecedor',
  fornecedor_catalogo: 'oferta(s) de fornecedor',
  catalogo_item: 'item(ns) de catalogo de cotacao',
  monitor_item: 'item(ns) do monitor de precos',
  produtos_historico: 'linha(s) de log',
  produto_status_google: 'status do Google',
  produto_fracionamento: 'fracionamento(s)',
};

interface CompraLinha { nota_id: number; data_emissao: string; nota_numero?: string | null; chave_acesso?: string | null; fornecedor_id?: number | null; fornecedor_nome?: string | null; cpf_cnpj?: string | null; codigo_fornecedor?: string | null; descricao?: string | null; valor_unitario: string | number }

interface ProdutoForm {
  codigo_interno: string;
  ean: string;
  referencia: string;
  descricao: string;
  descricao_complementar: string;
  outras_descricoes: string;
  grupo_id: number | '';
  categoria_id: number | '';
  marca_id: number | '';
  unidade_venda_id: number | '';
  preco_custo: string;
  preco_pix: string;
  ncm: string;
  cest: string;
  cfop: string;
  origem: string;
  cst_csosn: string;
  cst_pis: string;
  cst_cofins: string;
  situacao_tributaria_id: number | '';
  aliquota_icms: string;
  aliquota_ipi: string;
  aliquota_pis: string;
  aliquota_cofins: string;
  estoque_atual: string;
  estoque_minimo: string;
  unidade_compra: string;
  qtd_embalagem_padrao: string;
  unidade_conteudo_embalagem: string;
  peso_liquido: string;
  peso_bruto: string;
  permite_fracionado: boolean;
  fator_conversao: string;
  foto_url: string;
  status: 'ativo' | 'inativo';
  postar_catalogo: boolean;
  descricao_comercial: string;
  vantagens: string;
  observacoes_produto: string;
  uso_consumo: boolean;
}

const formVazio: ProdutoForm = {
  codigo_interno: '', ean: '', referencia: '', descricao: '', descricao_complementar: '', outras_descricoes: '',
  grupo_id: '', categoria_id: '', marca_id: '', unidade_venda_id: '',
  preco_custo: '', preco_pix: '',
  ncm: '', cest: '', cfop: '', origem: '0', cst_csosn: '', cst_pis: '', cst_cofins: '',
  situacao_tributaria_id: '',
  aliquota_icms: '', aliquota_ipi: '', aliquota_pis: '', aliquota_cofins: '',
  estoque_atual: '', estoque_minimo: '',
  unidade_compra: '', qtd_embalagem_padrao: '', unidade_conteudo_embalagem: '',
  peso_liquido: '', peso_bruto: '',
  permite_fracionado: false, fator_conversao: '',
  foto_url: '', status: 'ativo',
  postar_catalogo: false, descricao_comercial: '', vantagens: '',
  observacoes_produto: '',
  uso_consumo: false,
};

type Aba = 'fiscal' | 'observacoes' | 'fracionamento' | 'fornecedor' | 'cotacoes' | 'catalogo' | 'logs' | 'movimentacoes';

// Descricao Comercial do catalogo: primeira letra de cada palavra em maiuscula.
// So toca a 1a letra — preserva o resto (ex.: "PVC", "1,5mm", "LED" ficam intactos).
// Contador de uso por IA (mostrado embaixo de cada botao "Gerar via ...").
type UsoIAProvedor = { hoje: number; mes: number; total: number; custo_brl_mes: number };
type UsoIA = { claude: UsoIAProvedor; gemini: UsoIAProvedor; groq: UsoIAProvedor };

function fmtBRL(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Linha miuda sob o botao. Os gratis (Gemini/Groq) renovam a cota POR DIA → mostram
// "<hoje> hoje · gratis". O Claude e pago (gasto que acumula) → mostra "<mes> no mes"
// + custo estimado em R$. Hover sempre traz hoje / mes / total.
function MicroUsoIA({ dados, gratis }: { dados?: UsoIAProvedor; gratis: boolean }) {
  if (!dados) return <span className="text-[11px] leading-tight">&nbsp;</span>;
  const title = `Hoje: ${dados.hoje} · Mês: ${dados.mes} · Total: ${dados.total}`;
  return (
    <span className="text-[11px] leading-tight text-gray-500 text-center" title={title}>
      {gratis ? (
        <><b className="text-gray-700">{dados.hoje}</b> hoje · grátis</>
      ) : (
        <><b className="text-gray-700">{dados.mes}</b> no mês · <span style={{ color: 'var(--primary)' }}>~R$ {fmtBRL(dados.custo_brl_mes)}</span></>
      )}
    </span>
  );
}

function capitalizarPalavras(texto: string): string {
  return texto.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}

const CAMPOS_NUM = ['preco_pix', 'preco_custo', 'estoque_atual', 'estoque_minimo', 'aliquota_icms', 'aliquota_ipi', 'aliquota_pis', 'aliquota_cofins', 'peso_liquido', 'peso_bruto'] as const;

const TITULO_TRAVADO = 'So o administrador altera valor e estoque. A quantidade se corrige pela contagem da prateleira.';

/**
 * `'98.000'` -> `'98'` nos campos por prateleira ([DA-602]).
 *
 * Medido na tela: com o valor cru o campo de 58px precisa de **66px** e corta em `98,00` — o
 * numero fica MENTINDO, que e' pior que ficar feio. Normalizado, o pior caso real do acervo
 * (`4213`) sobra espaco. Nao alarga a coluna: os 58px foram calibrados com estouro zero ([DA-324]).
 * ⚠️ `null` continua `null` — e' "nunca contado", nao zero.
 */
function semZeroAtoaNosLugares(lugares: LocalProduto[]): LocalProduto[] {
  const limpar = (v: string | null) => {
    if (v === null || v === '') return v;
    const n = Number(v);
    return Number.isFinite(n) ? String(n) : v;
  };
  return lugares.map((l) => ({ ...l, quantidade: limpar(l.quantidade), quantidade_minima: limpar(l.quantidade_minima) }));
}

interface ModalRapidoState {
  tipo: string;
  titulo: string;
  endpoint: string;
  campos: CampoCadastroRapido[];
}

export function ProdutoForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id?: string }>();
  const toast = useToast();
  const dialog = useDialog();
  const { usuario } = useAuth();
  const podeEditarCatalogo = ['admin', 'gerente'].includes(usuario?.perfil ?? '');
  const ehAdmin = usuario?.perfil === 'admin';
  const isEditing = id !== undefined && id !== 'novo';
  // Valor e estoque so o admin EDITA (20/08/2026). `isEditing` faz parte da regra: produto NOVO
  // nasce com preco e saldo por quem cadastra — o que se proibe e' mexer no numero de um produto
  // que ja existe, porque e' isso que apaga a divergencia que a contagem deveria medir.
  const valorTravado = !ehAdmin && isEditing;
  // Vendedora e caixa nao gravam produto (o `PUT` e' de admin/gerente/estoquista), mas TODOS
  // preenchem as Outras Descricoes — decisao do Ruan, 02/10/2026. Pra eles a ficha fica travada
  // e o Salvar grava SO esse campo, pela porta propria. Travar o resto e' o que impede digitar
  // num campo que o Salvar nao levaria.
  const soOutrasDescricoes = isEditing && !['admin', 'gerente', 'estoquista'].includes(usuario?.perfil ?? '');
  const editandoId = isEditing ? Number(id) : null;
  const dadosClone = !isEditing && (location.state as any)?.clonarDe;

  const [form, setForm] = useState<ProdutoForm>(() => dadosClone ? { ...formVazio, ...dadosClone } : formVazio);
  const [carregando, setCarregando] = useState(!!editandoId);
  const [salvando, setSalvando] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [erroFoto, setErroFoto] = useState('');
  const [modalFotoAberto, setModalFotoAberto] = useState(false);
  // Selo MASTER da moldura: pergunta pela foto que ESTA na tela, a cada troca (enviar, acervo,
  // ajustar mudam a foto_url sem salvar). Mesma conta da coluna Qualidade da Foto.
  const [fotoMaster, setFotoMaster] = useState(false);
  // O recorte em alta da foto da moldura — e' ele que o "Ajustar foto" abre no editor.
  const [masterDaFoto, setMasterDaFoto] = useState<string | null>(null);
  const [avisoSubstituicao, setAvisoSubstituicao] = useState<string | null>(null);
  const [limpandoFoto, setLimpandoFoto] = useState(false);
  // Modo "usar foto de outro produto" — abre o seletor de fotos ja existentes.
  const [escolhendoFoto, setEscolhendoFoto] = useState(false);
  // URL da foto aberta no editor de enquadramento (foto nova = blob:, ou a atual)
  const [fotoParaEnquadrar, setFotoParaEnquadrar] = useState<string | null>(null);
  // O File cru da foto escolhida. Guardado pra poder subir o ORIGINAL quando o
  // editor for aplicado sem alteracao nenhuma (ver aplicarFotoEditada).
  const arquivoOriginalRef = useRef<File | null>(null);
  // Fotos PROVISORIAS que a tela da foto enviou e o produto ainda nao gravou (04/11/2026). O
  // "Aplicar" grava fora do acervo; so o Salvar do produto as leva pra la. Regra dele: "Se o usuario
  // nao salvou, a foto deve ser descartada" — antes, cada reaplicacao deixava uma copia solta no
  // acervo (102 copias medidas no dia).
  const fotosNaoSalvasRef = useRef<Set<string>>(new Set());
  // Nome no acervo e alvo de substituicao de cada provisoria — a moldura mostra o nome final.
  const provisoriasRef = useRef<Map<string, { nome: string; substituir: string | null }>>(new Map());
  // A foto que o produto tinha quando a tela da foto abriu — fechar sem Salvar volta pra ela.
  const fotoAoAbrirModalRef = useRef('');
  // Abre direto numa aba quando a URL traz ?aba=... (ex.: vindo do painel Catalogo Digital)
  const [aba, setAba] = useState<Aba>(
    new URLSearchParams(location.search).get('aba') === 'catalogo' ? 'catalogo' : 'observacoes',
  );
  const [menuAberto, setMenuAberto] = useState(false);
  const [gerandoIA, setGerandoIA] = useState<'claude' | 'gemini' | 'groq' | null>(null);
  const [usoIA, setUsoIA] = useState<UsoIA | null>(null);
  const sticky = useStickyHeader();
  const menuRef = useRef<HTMLDivElement>(null);

  const [grupos, setGrupos] = useState<OpAux[]>([]);
  const [categorias, setCategorias] = useState<OpAux[]>([]);
  const [marcas, setMarcas] = useState<OpAux[]>([]);
  const [unidades, setUnidades] = useState<OpUnidade[]>([]);
  const [sitTribs, setSitTribs] = useState<OpSitTrib[]>([]);

  const [modalRapido, setModalRapido] = useState<ModalRapidoState | null>(null);
  const [historico, setHistorico] = useState<HistoricoLinha[] | null>(null);
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoLinha[] | null>(null);
  const [compras, setCompras] = useState<CompraLinha[]>([]);
  const [descFornecedor, setDescFornecedor] = useState<string>('');
  // Se preenchido, este produto e filho de fracionamento: estoque vira read-only (formula).
  const [fracPai, setFracPai] = useState<FracPaiInfo | null>(null);
  const [estoqueEfetivo, setEstoqueEfetivo] = useState<string | null>(null);
  // Compra em aberto (DA-368) — so-leitura, exibida como historico "Ultima compra" no bloco
  // de estoque. Nao e coluna do `form` (nao se edita, nao se salva por aqui).
  const [ultimaCompra, setUltimaCompra] = useState<{ em: string; fornecedor: string | null } | null>(null);
  // Onde o produto mora (DA-324). Estado SEPARADO do `form`: nao e coluna de `produtos`, e a relacao
  // N — mas salva junto (e o Salvar que valida a principal).
  const [locais, setLocais] = useState<LocalProduto[]>([]);
  // Metas de Margem (GET /api/margens/mapa): padrao da loja + meta por categoria.
  const [mapaMetas, setMapaMetas] = useState<{ padrao: number; categorias: Record<string, number> } | null>(null);
  // Marcas de COMPRA (DA-386). Estado SEPARADO do `form` (nao e coluna de `produtos`, e relacao N),
  // mas salva junto. Guarda so os ids; o nome vem de `marcas`. Eixo distinto do `marca_id` (exibicao).
  const [marcasCompra, setMarcasCompra] = useState<number[]>([]);
  // A porta de ajuste do estoque ([DA-602]) — aberta pelo clique no `Est. Atual` de quem nao e admin.
  const [ajustandoEstoque, setAjustandoEstoque] = useState(false);

  const formOriginal = useRef<string | null>(null);
  // Valor gravado das Outras Descricoes — pra quem so pode mexer nelas, e' o unico comparavel.
  const outrasOriginal = useRef('');

  // Carregar dados auxiliares
  useEffect(() => {
    Promise.all([
      api.get<OpAux[]>('/api/grupos'),
      api.get<OpAux[]>('/api/categorias'),
      api.get<OpAux[]>('/api/marcas'),
      api.get<OpUnidade[]>('/api/unidades-medida'),
      api.get<OpSitTrib[]>('/api/situacoes-tributarias'),
    ]).then(([g, c, m, u, st]) => {
      setGrupos(g); setCategorias(c); setMarcas(m); setUnidades(u); setSitTribs(st);
    }).catch(() => {});
    // Separado de proposito: sem o mapa a ficha funciona igual, so nao marca a meta.
    api.get<{ padrao: number; categorias: Record<string, number> }>('/api/margens/mapa')
      .then(setMapaMetas).catch(() => {});
  }, []);

  // Selo MASTER: zera na troca (nunca afirma Master de uma foto que ainda nao foi conferida) e
  // descarta resposta de uma foto que ja saiu da moldura.
  useEffect(() => {
    setFotoMaster(false);
    setMasterDaFoto(null);
    if (!form.foto_url) return;
    // Provisoria: so existe porque passou na regua no envio, e o recorte dela mora ao lado.
    if (ehProvisoria(form.foto_url)) {
      setFotoMaster(true);
      setMasterDaFoto(form.foto_url.replace(/\.webp$/, '.master.webp'));
      return;
    }
    let vivo = true;
    api.get<{ master_completo: boolean; master_url: string | null }>(`/api/produtos/foto-master?url=${encodeURIComponent(form.foto_url)}`)
      .then((r) => { if (vivo) { setFotoMaster(r.master_completo === true); setMasterDaFoto(r.master_url); } })
      .catch(() => {});
    return () => { vivo = false; };
  }, [form.foto_url]);

  // Ajuste que vai SUBSTITUIR um arquivo do acervo: a tela da foto diz, antes do Salvar, quantos
  // produtos passam a mostrar a versao nova — a substituicao vale pra todos que usam o arquivo.
  useEffect(() => {
    setAvisoSubstituicao(null);
    const alvo = ehProvisoria(form.foto_url) ? provisoriasRef.current.get(form.foto_url)?.substituir : null;
    if (!alvo) return;
    let vivo = true;
    api.get<{ total: number }>('/api/produtos/foto-usos', { query: { url: alvo } })
      .then(({ total }) => {
        if (!vivo) return;
        setAvisoSubstituicao(
          `Ao salvar, o arquivo ${nomeDoArquivo(alvo)} sera substituido` +
            (total > 1 ? ` — vale para os ${total} produtos que usam esta foto.` : '.'),
        );
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [form.foto_url]);

  // Carregar produto
  useEffect(() => {
    if (!editandoId) return;
    setCarregando(true);
    canetaRef.current = 'aguardando';
    setEdicaoDeOutro(null);
    api.get<any>(`/api/produtos/${editandoId}`)
      .then((p) => {
        const loaded: ProdutoForm = {
          codigo_interno: p.codigo_interno ?? '',
          ean: p.ean ?? '',
          referencia: p.referencia ?? '',
          descricao: p.descricao ?? '',
          descricao_complementar: p.descricao_complementar ?? '',
          outras_descricoes: p.outras_descricoes ?? '',
          grupo_id: p.grupo_id ?? '',
          categoria_id: p.categoria_id ?? '',
          marca_id: p.marca_id ?? '',
          unidade_venda_id: p.unidade_venda_id ?? '',
          preco_custo: p.preco_custo != null ? String(p.preco_custo) : '',
          preco_pix: p.preco_pix != null ? String(p.preco_pix) : '',
          ncm: p.ncm ?? '',
          cest: p.cest ?? '',
          cfop: p.cfop ?? '',
          origem: p.origem ?? '0',
          cst_csosn: p.cst_csosn ?? '',
          cst_pis: p.cst_pis ?? '',
          cst_cofins: p.cst_cofins ?? '',
          situacao_tributaria_id: p.situacao_tributaria_id ?? '',
          aliquota_icms: p.aliquota_icms != null ? String(p.aliquota_icms) : '',
          aliquota_ipi: p.aliquota_ipi != null ? String(p.aliquota_ipi) : '',
          aliquota_pis: p.aliquota_pis != null ? String(p.aliquota_pis) : '',
          aliquota_cofins: p.aliquota_cofins != null ? String(p.aliquota_cofins) : '',
          estoque_atual: p.estoque_atual != null ? String(p.estoque_atual) : '',
          estoque_minimo: p.estoque_minimo != null ? String(p.estoque_minimo) : '',
          unidade_compra: p.unidade_compra ?? '',
          qtd_embalagem_padrao: p.qtd_embalagem_padrao != null ? formatQtd(p.qtd_embalagem_padrao) : '',
          unidade_conteudo_embalagem: p.unidade_conteudo_embalagem ?? p.unidade_sigla ?? '',
          peso_liquido: p.peso_liquido != null ? String(p.peso_liquido) : '',
          peso_bruto: p.peso_bruto != null ? String(p.peso_bruto) : '',
          permite_fracionado: !!p.permite_fracionado,
          fator_conversao: p.fator_conversao != null ? String(p.fator_conversao) : '',
          foto_url: p.foto_url ?? '',
          status: (p.status === 'inativo' ? 'inativo' : 'ativo'),
          postar_catalogo: !!p.postar_catalogo,
          descricao_comercial: p.descricao_comercial ?? '',
          vantagens: p.vantagens ?? '',
          observacoes_produto: p.observacoes_produto ?? '',
          uso_consumo: !!p.uso_consumo,
        };
        setForm(loaded);
        setDescFornecedor(p.descricao_fornecedor ?? '');
        setFracPai(p.fracionamento_pai ?? null);
        setEstoqueEfetivo(p.estoque_efetivo != null ? String(p.estoque_efetivo) : null);
        setUltimaCompra(p.ultima_compra_em ? { em: p.ultima_compra_em, fornecedor: p.ultima_compra_fornecedor_nome ?? null } : null);
        // ⚠️ Normaliza AQUI, na carga, e nao no `value` do input: o Postgres devolve `numeric` com
        // 3 casas (`'98.000'`) e nos 58px da coluna isso renderiza **truncado** (`98,00`) — o
        // estoquista le uma quantidade que nao existe. Normalizar no render seria pior: o campo e'
        // controlado, entao a cada tecla o texto digitado seria reescrito e o cursor pularia.
        const locaisNormalizados = semZeroAtoaNosLugares(p.localizacoes ?? []);
        setLocais(locaisNormalizados);
        setMarcasCompra(p.marcas_compra ?? []);
        formOriginal.current = estadoComparavel(loaded, locaisNormalizados, p.marcas_compra ?? []);
        outrasOriginal.current = loaded.outras_descricoes;
        void reivindicarNaAbertura(editandoId);
      })
      .catch((e) => toast.erro(e.message))
      .finally(() => setCarregando(false));
  }, [editandoId, toast]);

  // --- Caneta da ficha (migration 0316, a DA-406 do orcamento sem o "assumir") ----------------
  // Quem abre primeiro edita; quem chega depois e' avisado e fica em somente leitura, ate dar F5
  // depois que o outro sair (pedido do Ruan, 09/10/2026: so isso, ninguem toma a caneta).
  //   'minha'   — seguro a caneta e renovo.
  //   'solta'   — era minha e devolvi porque a aba foi pro fundo sem nada pendente; pego de volta
  //               quando ela reaparecer. Sem isto uma aba esquecida seguraria o produto pra sempre.
  //   'deOutro' — somente leitura. Nunca bate nem solta: um leitor que soltasse ao fechar a aba
  //               derrubaria a trava de quem esta editando de verdade.
  //   'aguardando' — a ficha ainda nao carregou; quem pede e' a abertura, o renovador nao mexe.
  const canetaRef = useRef<'aguardando' | 'minha' | 'solta' | 'deOutro'>('aguardando');
  const [edicaoDeOutro, setEdicaoDeOutro] = useState<string | null>(null);
  const somenteLeitura = edicaoDeOutro !== null;

  function entrarEmSomenteLeitura(dono: string) {
    canetaRef.current = 'deOutro';
    setEdicaoDeOutro(dono);
  }

  // Devolve o nome de quem esta com a ficha, ou null se a caneta ficou comigo. Rede fora NAO tranca a
  // tela: o PUT tem a trava real, entao no maximo o Salvar e' recusado — melhor que impedir de
  // trabalhar por erro de infra.
  async function pedirCaneta(produtoId: number): Promise<string | null> {
    try {
      const r = await api.post<{ ok: boolean; por_nome?: string }>(`/api/produtos/${produtoId}/editando`);
      if (r.ok) {
        canetaRef.current = 'minha';
        return null;
      }
      return r.por_nome || 'outro usuario';
    } catch {
      canetaRef.current = 'minha';
      return null;
    }
  }

  async function reivindicarNaAbertura(produtoId: number) {
    const dono = await pedirCaneta(produtoId);
    if (!dono) return;
    entrarEmSomenteLeitura(dono);
    await dialog.alerta(
      `Este produto esta sendo editado por ${dono} agora.\n\nVoce pode consultar, mas nao alterar. Quando ${dono} sair, atualize a tela (F5) para editar.`,
      { titulo: 'Somente leitura', tipo: 'aviso' },
    );
  }

  // Perdeu a caneta com a ficha aberta (a aba voltou do fundo e outra pessoa tinha entrado, ou o
  // Salvar foi recusado). Texto proprio: aqui a pessoa estava trabalhando.
  async function perderCaneta(dono: string) {
    if (canetaRef.current === 'deOutro') return;
    // A frase da perda so quando HA o que perder: a aba que soltou a caneta no fundo nao tinha nada
    // pendente (foi por isso que soltou), e ali ela so assustaria.
    const perdeu = alteradoRef.current ? ' — o que nao foi salvo aqui nao pode mais ser gravado' : '';
    entrarEmSomenteLeitura(dono);
    await dialog.alerta(
      `${dono} esta editando este produto agora.\n\nEsta ficha passou para somente leitura${perdeu}. Quando ${dono} sair, atualize a tela (F5) para editar.`,
      { titulo: 'Somente leitura', tipo: 'aviso' },
    );
  }

  // Carregar historico de compras (para aba "Fornecedor de Compra")
  useEffect(() => {
    if (!editandoId) return;
    api.get<CompraLinha[]>(`/api/produtos/${editandoId}/compras`).then((r) => setCompras(r || [])).catch(() => setCompras([]));
  }, [editandoId]);

  // Contador de uso por IA — carrega quando a aba Catalogo esta aberta (re-carrega
  // sozinho depois de cada geracao, em gerarCatalogoIA).
  useEffect(() => {
    if (!editandoId || aba !== 'catalogo') return;
    api.get<UsoIA>('/api/produtos/catalogo-ia/uso').then(setUsoIA).catch(() => {});
  }, [editandoId, aba]);

  // Carregar historico (lazy quando abre a aba Logs)
  useEffect(() => {
    if (!editandoId || aba !== 'logs' || historico !== null) return;
    api.get<HistoricoLinha[]>(`/api/produtos/${editandoId}/historico`).then((r) => setHistorico(r || [])).catch(() => setHistorico([]));
  }, [aba, editandoId, historico]);

  // Carregar movimentacoes de estoque (lazy quando abre a aba Movimentacoes)
  useEffect(() => {
    if (!editandoId || aba !== 'movimentacoes' || movimentacoes !== null) return;
    api.get<MovimentacaoLinha[]>(`/api/produtos/${editandoId}/movimentacoes`)
      .then((r) => setMovimentacoes(r || []))
      .catch(() => setMovimentacoes([]));
  }, [aba, editandoId, movimentacoes]);

  // O componente é reutilizado ao navegar entre /produtos/:id (ex: edit → novo),
  // então o useState não re-executa. Reinicializa o form quando a rota muda.
  useEffect(() => {
    if (editandoId) return;
    const clone = (location.state as any)?.clonarDe;
    setForm(clone ? { ...formVazio, ...clone } : formVazio);
    formOriginal.current = null;
  // location.key muda a cada navegação — garante reset correto
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Fechar menu ⋮ ao clicar fora
  useEffect(() => {
    if (!menuAberto) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuAberto]);

  // Esc volta pra lista (skip se foco em input)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modalRapido) return;
      const tgt = e.target as HTMLElement;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.tagName === 'SELECT')) {
        tgt.blur();
        return;
      }
      navigate('/produtos');
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalRapido, navigate]);

  function up<K extends keyof ProdutoForm>(k: K, v: ProdutoForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // Rascunha descricao comercial + vantagens via IA (analisa dados + foto do produto).
  // provedor 'claude' = nossa IA; 'gemini' = IA do Google; 'groq' = Llama (free).
  async function gerarCatalogoIA(provedor: 'claude' | 'gemini' | 'groq') {
    if (!editandoId) return;
    if (form.descricao_comercial.trim() || form.vantagens.trim()) {
      const ok = await dialog.confirmar(
        'Isso vai substituir a descricao comercial e as vantagens atuais por um rascunho da IA. Continuar?',
      );
      if (!ok) return;
    }
    setGerandoIA(provedor);
    try {
      const r = await api.post<{ descricao_comercial: string; vantagens: string; usou_imagem: boolean; provedor: string }>(
        `/api/produtos/${editandoId}/gerar-catalogo-ia`,
        { provedor },
      );
      up('descricao_comercial', capitalizarPalavras(r.descricao_comercial));
      up('vantagens', r.vantagens);
      const nomeIA = r.provedor === 'gemini' ? 'Google' : r.provedor === 'groq' ? 'Groq' : 'Claude';
      toast.sucesso(
        r.usou_imagem
          ? `Rascunho gerado pela IA ${nomeIA} (analisou a foto). Revise antes de salvar.`
          : `Rascunho gerado pela IA ${nomeIA}. Revise antes de salvar.`,
      );
      // Atualiza o contador embaixo dos botoes com o uso recem-registrado.
      api.get<UsoIA>('/api/produtos/catalogo-ia/uso').then(setUsoIA).catch(() => {});
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gerar via IA');
    } finally {
      setGerandoIA(null);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value, type } = e.target;
    const isNum = type === 'number' || (CAMPOS_NUM as readonly string[]).includes(name);
    const v = isNum ? value : value.toUpperCase();
    if (name === 'estoque_atual') { mudarEstoqueAtual(v); return; }
    setForm((prev) => ({ ...prev, [name]: v }));
  }

  function mudarEstoqueAtual(v: string) {
    setForm((prev) => ({ ...prev, estoque_atual: v }));
    // Espelho do lugar unico ([DA-366]): com UMA prateleira, ela e o Est. Atual — o mesmo numero em
    // dois campos da mesma tela. Deixa-los divergir so produziria o "Estoque incoerente" da [DA-339]
    // no Salvar, por um estado que a propria tela criou.
    if (locais.length === 1) {
      setLocais((prev) => prev.map((l) => ({ ...l, quantidade: v === '' ? null : v })));
    }
  }

  // O outro sentido do espelho, mais o caso do lugar recem-adicionado: ele nasce em `—` e ja mostra
  // o total, em vez de so mostrar depois que o backend gravar ([DA-366]).
  function aplicarLocais(novos: LocalProduto[]) {
    if (novos.length === 1) {
      const q = novos[0]!.quantidade;
      if (q === null) {
        setLocais(novos.map((l) => ({ ...l, quantidade: form.estoque_atual === '' ? null : form.estoque_atual })));
        return;
      }
      if (Number(q) !== Number(form.estoque_atual)) setForm((f) => ({ ...f, estoque_atual: q }));
    }
    setLocais(novos);
  }

  const precoCartaoCalc = useMemo(() => calcPrecoCartao(parseFloat(form.preco_pix) || 0), [form.preco_pix]);

  function margemPix() { return formatMargem(parseFloat(form.preco_pix) || 0, parseFloat(form.preco_custo) || 0); }
  // Meta de markup que vale pra ESTA ficha: a da categoria escolhida na tela (troca junto se a
  // categoria trocar), senao a padrao da loja. null enquanto o mapa nao chegou — ai nada e' marcado.
  const metaPix = mapaMetas && form.categoria_id !== undefined
    ? metaMarkupEfetiva(form.categoria_id === '' ? null : mapaMetas.categorias[String(form.categoria_id)], mapaMetas.padrao)
    : null;
  const pixAbaixoDaMeta = metaPix !== null && abaixoDaMeta(parseFloat(form.preco_pix) || 0, parseFloat(form.preco_custo) || 0, metaPix);
  function margemCartao() { return formatMargem(precoCartaoCalc, parseFloat(form.preco_custo) || 0); }

  // Os lugares entram na comparacao: eles salvam junto com o produto mas moram FORA do `form`, e sem
  // isso mexer so na localizacao deixaria o botao Salvar desabilitado ("nenhuma alteracao pra salvar"
  // com alteracao na tela).
  const estadoComparavel = (f: typeof form, l: LocalProduto[], mc: number[]) => JSON.stringify({
    f, l: l.map((x) => ({ id: x.localizacao_id, p: x.principal, q: x.quantidade, m: x.quantidade_minima })),
    mc: [...mc].sort((a, b) => a - b),
  });
  const formAlterado = soOutrasDescricoes
    ? form.outras_descricoes !== outrasOriginal.current
    : !isEditing || !formOriginal.current || estadoComparavel(form, locais, marcasCompra) !== formOriginal.current;

  // Liga no instante em que ele confirma o descarte, pra o aviso do navegador nao
  // repetir a pergunta que o modal acabou de fazer.
  const descartandoRef = useRef(false);

  /**
   * Guarda de saida pelo NAVEGADOR: F5, fechar a aba, fechar a janela, botao Voltar.
   *
   * ⚠️ So cobre a saida do navegador. A seta e o Cancelar da tela passam pelo `fechar()`
   * (que tem confirmacao propria, com texto nosso); ja trocar de tela pelo MENU do topo
   * nao da pra prender — `useBlocker` do react-router exige o roteador de dados, e o
   * sistema inteiro roda em `<BrowserRouter>`. Migrar aquilo mexe em toda tela.
   *
   * O texto e do navegador, nao nosso: desde 2016 o Chrome ignora a mensagem custom.
   */
  useEffect(() => {
    if (!isEditing || !formAlterado) return;
    const avisar = (e: BeforeUnloadEvent) => {
      // Ja perguntou e ele escolheu descartar — nao perguntar de novo. Sem isso o
      // operador leva DOIS avisos quando o `navigate(-1)` sai do site (ficha aberta
      // por URL direta, ou depois de um F5 nela): o nosso e, em seguida, o texto
      // generico do navegador. Medido na release publicada em 02/09/2026.
      if (descartandoRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [isEditing, formAlterado]);

  // Renova a caneta a cada 30s enquanto a ficha esta NA TELA ou com alteracao nao salva; aba que vai
  // pro fundo sem nada pendente devolve na hora e pega de volta ao reaparecer. Devolve tambem ao
  // sair da tela e ao fechar a aba.
  const alteradoRef = useRef(false);
  alteradoRef.current = formAlterado;
  useEffect(() => {
    if (!editandoId) return;
    const devolver = () => {
      if (canetaRef.current !== 'minha') return;
      canetaRef.current = 'solta';
      // `keepalive` faz o pedido sobreviver ao fechamento da aba — sem ele o navegador aborta o
      // fetch e a caneta so cairia quando expirasse (2 min de espera pro colega).
      fetch(`/api/produtos/${editandoId}/editando`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
        keepalive: true,
      }).catch(() => {});
    };
    const bater = () => {
      const visivel = document.visibilityState === 'visible';
      if (canetaRef.current === 'minha') {
        if (!visivel && !alteradoRef.current) { devolver(); return; }
        api.post<{ ok: boolean; por_nome?: string }>(`/api/produtos/${editandoId}/editando`)
          .then((r) => { if (r && !r.ok) void perderCaneta(r.por_nome || 'outro usuario'); })
          .catch(() => {});
      } else if (canetaRef.current === 'solta' && visivel) {
        void pedirCaneta(editandoId).then((dono) => { if (dono) void perderCaneta(dono); });
      }
    };
    const tick = setInterval(bater, 30_000);
    document.addEventListener('visibilitychange', bater);
    // `pagehide`, e nao `beforeunload`: o beforeunload dispara ANTES da pergunta "sair sem salvar?"
    // desta ficha, e quem desistisse de sair ficaria editando sem a caneta.
    window.addEventListener('pagehide', devolver);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', bater);
      window.removeEventListener('pagehide', devolver);
      devolver();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editandoId]);

  /**
   * O modal de ajuste ([DA-602]) grava por conta propria e parte do que esta GRAVADO. Se a ficha
   * tiver mexida NAO SALVA no estoque ou nas prateleiras, ele abriria afirmando um "Quantidade
   * atual" que o banco nao tem — e ao salvar sobrescreveria a edicao aberta sem avisar.
   *
   * ⚠️ Compara SO estoque e lugares, de proposito: quem mexeu na descricao e quer ajustar o estoque
   * nao pode ser barrado por isso. O buraco nasceu junto com o botao pro admin — enquanto o campo
   * era read-only (so o estoquista via a porta) nao havia como os dois discordarem.
   */
  const estoqueAlteradoNaFicha = (() => {
    if (!isEditing || !formOriginal.current) return false;
    try {
      const orig = JSON.parse(formOriginal.current) as { f: typeof form; l: { id: number; p: boolean; q: string | null; m: string | null }[] };
      if (String(orig.f.estoque_atual ?? '') !== String(form.estoque_atual ?? '')) return true;
      const agora = locais.map((x) => ({ id: x.localizacao_id, p: x.principal, q: x.quantidade, m: x.quantidade_minima }));
      return JSON.stringify(orig.l) !== JSON.stringify(agora);
    } catch { return false; }
  })();

  function abrirCadastroRapido(tipo: string) {
    const opcoesCategorias = categorias.map((c) => ({ value: c.id, label: c.nome }));
    const modais: Record<string, ModalRapidoState> = {
      grupo: { tipo, titulo: 'Novo Grupo', endpoint: '/api/grupos', campos: [{ key: 'nome', label: 'Nome', obrigatorio: true }] },
      categoria: { tipo, titulo: 'Nova Categoria', endpoint: '/api/categorias', campos: [{ key: 'nome', label: 'Nome', obrigatorio: true }] },
      marca: { tipo, titulo: 'Nova Marca', endpoint: '/api/marcas', campos: [{ key: 'nome', label: 'Nome', obrigatorio: true }] },
      unidade: {
        tipo, titulo: 'Nova Unidade', endpoint: '/api/unidades-medida',
        campos: [
          { key: 'sigla', label: 'Sigla', obrigatorio: true },
          { key: 'nome', label: 'Nome', obrigatorio: true },
          { key: 'permite_fracionado', label: 'Permite fracionado', tipo: 'boolean', descricao: 'Permite venda fracionada' },
        ],
      },
      unidade_compra: {
        tipo, titulo: 'Nova Unidade', endpoint: '/api/unidades-medida',
        campos: [
          { key: 'sigla', label: 'Sigla', obrigatorio: true },
          { key: 'nome', label: 'Nome', obrigatorio: true },
          { key: 'permite_fracionado', label: 'Permite fracionado', tipo: 'boolean', descricao: 'Permite venda fracionada' },
        ],
      },
      situacao_tributaria: {
        tipo, titulo: 'Nova Situacao Tributaria', endpoint: '/api/situacoes-tributarias',
        campos: [
          { key: 'codigo', label: 'Codigo', obrigatorio: true },
          { key: 'tipo', label: 'Tipo', tipo: 'select', obrigatorio: true,
            opcoes: [
              { value: 'ICMS', label: 'ICMS' },
              { value: 'ST', label: 'Substituicao Tributaria' },
              { value: 'ISENTO', label: 'Isento' },
              { value: 'NI', label: 'Nao Incidencia' },
            ],
          },
          { key: 'descricao', label: 'Descricao', obrigatorio: true },
          { key: 'aliquota', label: 'Aliquota (%)', tipo: 'number', step: '0.01' },
        ],
      },
    };
    // suprimir aviso "unused" se categorias mudarem no futuro
    void opcoesCategorias;
    setModalRapido(modais[tipo] ?? null);
  }

  function aplicarCadastroRapido(tipo: string, novo: any) {
    if (tipo === 'grupo') {
      setGrupos((p) => [...p, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
      up('grupo_id', novo.id);
    } else if (tipo === 'categoria') {
      setCategorias((p) => [...p, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
      up('categoria_id', novo.id);
    } else if (tipo === 'marca') {
      setMarcas((p) => [...p, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
      up('marca_id', novo.id);
    } else if (tipo === 'unidade') {
      setUnidades((p) => [...p, novo]);
      up('unidade_venda_id', novo.id);
    } else if (tipo === 'unidade_compra') {
      setUnidades((p) => [...p, novo]);
      up('unidade_compra', novo.sigla);
    } else if (tipo === 'situacao_tributaria') {
      setSitTribs((p) => [...p, novo]);
      up('situacao_tributaria_id', novo.id);
    }
    setModalRapido(null);
  }

  // Foto: upload e drag-drop
  // Sobe o arquivo como foto PROVISORIA (fora do acervo ate o Salvar) e retorna o endereco dela.
  // `nome` = nome do arquivo original, que vira o nome no acervo; `substituir` = a foto que o
  // "Ajustar foto" vai trocar; `intacto` = aplicada sem mexer no editor (o original e' guardado como veio).
  async function uploadFile(
    file: File | null | undefined,
    extras: { nome?: string; substituir?: string; intacto?: boolean },
  ): Promise<string | null> {
    if (!file) return null;
    if (file.size > LIMITE_FOTO_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      setErroFoto(`A imagem tem ${mb} MB e excede o limite de ${LIMITE_FOTO_MB} MB.`);
      return null;
    }
    setEnviandoFoto(true);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      if (extras.nome) formData.append('nome', extras.nome);
      if (extras.substituir) formData.append('substituir', extras.substituir);
      if (extras.intacto) formData.append('intacto', '1');
      const token = localStorage.getItem('fiodecobre.token');
      const res = await fetch('/api/upload/foto-ficha', {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const data: any = await res.json().catch(() => ({}));
        // O portao do master (422) devolve os motivos em pt-BR, ja dizendo o que
        // fazer. Sem isso o operador so veria "erro ao enviar imagem" e nao teria
        // como saber que a foto foi recusada por resolucao.
        if (Array.isArray(data?.motivos) && data.motivos.length) {
          throw new Error(data.motivos.join(' '));
        }
        throw new Error(data.erro || 'erro ao enviar imagem');
      }
      const data = (await res.json()) as { url: string; nome: string; substituir: string | null };
      provisoriasRef.current.set(data.url, { nome: data.nome, substituir: data.substituir });
      up('foto_url', data.url);
      return data.url;
    } catch (e: any) {
      setErroFoto(e.message || 'erro ao enviar imagem');
      return null;
    } finally {
      setEnviandoFoto(false);
    }
  }

  // Foto nova (selecionada, arrastada ou colada): nao sobe direto — abre o
  // editor de enquadramento, pra posicionar o produto no quadro sem que ele
  // estoure a moldura. O editor NAO reamostra: a foto sai na resolucao que
  // entrou (ver EditorEnquadrarFoto).
  // TODA foto nova passa pelo editor — decisao dele em 13/08/2026, depois de uma
  // versao que pulava a etapa pra foto ja pronta: "Nao pode pular o editor, se
  // nao, nao consigo redimensionar". O atalho tirava dele o unico lugar onde da
  // pra endireitar, aproximar ou girar antes de gravar.
  function prepararFotoNova(file: File | null | undefined) {
    if (!file) return;
    setErroFoto('');
    // Confere contra o teto da ORIGEM, nao o do upload: este arquivo nao sobe
    // como esta — o editor o devolve reencodado em WebP, bem menor.
    if (file.size > LIMITE_ORIGEM_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      setErroFoto(`A imagem tem ${mb} MB — grande demais pra abrir no editor.`);
      return;
    }
    arquivoOriginalRef.current = file;
    setFotoParaEnquadrar(URL.createObjectURL(file));
    abrirModalFoto();
  }

  // "Trocar foto" de dentro da tela da foto tambem passa por aqui: a foto de referencia so se
  // guarda quando a tela ABRE, senao a troca viraria o ponto de volta.
  function abrirModalFoto() {
    if (!modalFotoAberto) fotoAoAbrirModalRef.current = form.foto_url;
    setModalFotoAberto(true);
  }

  // Descarta no servidor toda foto provisoria nao salva, menos a que fica na tela. Falha nao
  // interrompe nada: a pasta provisoria se limpa sozinha em 24h, e ela nao entra no acervo.
  function descartarFotosNaoSalvas(manter: string) {
    for (const url of [...fotosNaoSalvasRef.current]) {
      if (url === manter) continue;
      fotosNaoSalvasRef.current.delete(url);
      api.post('/api/upload/foto-ficha/descartar', { url })
        .catch((e) => console.warn('[foto-ficha/descartar]', url, e?.message));
    }
  }

  // Sair da ficha com a tela da foto aberta (menu, voltar do navegador) tambem e "nao salvou".
  useEffect(() => () => descartarFotosNaoSalvas(''), []);

  // Fechar a tela da foto sem Salvar desfaz o que foi feito nela.
  function fecharModalFotoSemSalvar() {
    setModalFotoAberto(false);
    setEscolhendoFoto(false);
    fecharEnquadramento();
    const volta = fotoAoAbrirModalRef.current;
    if (form.foto_url !== volta) up('foto_url', volta);
    descartarFotosNaoSalvas(volta);
  }

  function handleUploadFoto(e: React.ChangeEvent<HTMLInputElement>) {
    prepararFotoNova(e.target.files?.[0]);
    e.target.value = ''; // permite selecionar o mesmo arquivo de novo
  }
  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) prepararFotoNova(file);
  }
  function handleDragOver(e: React.DragEvent<HTMLDivElement>) { e.preventDefault(); setDragging(true); }
  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) { e.preventDefault(); setDragging(false); }
  // Colar com Ctrl+V saiu em 04/11/2026: a foto colada nao tem nome de arquivo, e o nome do
  // arquivo virou o nome da foto no acervo. Toda foto entra pronta, pelo arquivo.

  // So DESVINCULA a foto deste produto — nao apaga o arquivo do disco. Outra(s)
  // fichas podem estar usando a mesma foto (reaproveitamento); apagar o arquivo
  // quebraria essas. Arquivo orfao e barato (~100KB); foto compartilhada apagada
  // por engano nao tem volta.
  function handleRemoverFoto() {
    if (!form.foto_url) return;
    up('foto_url', '');
    descartarFotosNaoSalvas('');
  }

  // Fecha o editor de enquadramento, liberando o blob da foto nova (se houver).
  function fecharEnquadramento() {
    if (fotoParaEnquadrar && fotoParaEnquadrar.startsWith('blob:')) URL.revokeObjectURL(fotoParaEnquadrar);
    setFotoParaEnquadrar(null);
    arquivoOriginalRef.current = null;
  }

  // Apos editar uma foto: se ela era compartilhada por outros produtos, pergunta
  // se a alteracao vale pra todos eles ou so pra este. "todos" -> atualiza o
  // foto_url dos irmaos no banco na hora.
  async function talvezAplicarEmTodos(urlAntiga: string, urlNova: string) {
    if (!urlAntiga || urlAntiga === urlNova) return;
    try {
      const query: Record<string, string> = { url: urlAntiga };
      if (editandoId) query.excluir_id = String(editandoId);
      const { total } = await api.get<{ total: number }>('/api/produtos/foto-usos', { query });
      if (total < 1) return; // foto exclusiva deste produto — nada a perguntar
      const todos = await dialog.confirmar(
        `Esta foto e usada por outros ${total} produto(s). Aplicar a alteracao em todos eles tambem, ou deixar so neste produto?`,
        {
          titulo: 'Foto compartilhada',
          textoConfirmar: `Aplicar em todos (${total})`,
          textoCancelar: 'So neste produto',
        },
      );
      if (!todos) return;
      const r = await api.post<{ atualizados: number }>('/api/produtos/foto-aplicar-em-lote', {
        url_antiga: urlAntiga,
        url_nova: urlNova,
      });
      toast.sucesso(`Foto aplicada em ${r.atualizados} produto(s)`);
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao aplicar a foto nos outros produtos');
    }
  }

  // Recebe a foto enquadrada e a sobe como foto PROVISORIA. Dois casos:
  //   - Foto NOVA (Trocar foto / Enviar foto / arrastar): leva o nome do arquivo original, que vira
  //     o nome no acervo; nome repetido o servidor recusa.
  //   - AJUSTE da foto da moldura ("Ajustar foto"): substitui o arquivo dela no Salvar — no mesmo
  //     endereco, valendo pra todos os produtos que a usam (decisao dele, 04/11/2026). Por isso a
  //     pergunta "aplicar em todos?" nao aparece mais aqui.
  //
  // ⚠️ `semAlteracao` = ele abriu o editor e aplicou sem mexer em nada. Foto nova: sobe o ARQUIVO
  // ORIGINAL, que o servidor guarda intacto como master (DA-479 — passo de conveniencia nao pode
  // descartar o original). Ajuste sem alteracao: nao ha o que gravar.
  async function aplicarFotoEditada(blob: Blob, semAlteracao?: boolean) {
    const urlAntiga = form.foto_url;
    const original = arquivoOriginalRef.current;
    if (!original && semAlteracao) {
      fecharEnquadramento();
      return;
    }
    const arquivo = semAlteracao && original
      ? original
      : new File([blob], `foto-editada.${blob.type === 'image/png' ? 'png' : 'webp'}`, { type: blob.type });
    const urlNova = await uploadFile(
      arquivo,
      original ? { nome: original.name, intacto: !!semAlteracao } : { substituir: urlAntiga },
    );
    fecharEnquadramento();
    if (!urlNova) return;
    fotosNaoSalvasRef.current.add(urlNova);
    descartarFotosNaoSalvas(urlNova);
  }

  // Otimizacao da foto (endpoint /otimizar-foto): o backend ja gravou a foto
  // nova neste produto; aqui atualiza o form e oferece propagar pros irmaos.
  async function aoOtimizarFoto(urlNova: string) {
    const urlAntiga = form.foto_url;
    up('foto_url', urlNova);
    // O servidor ja gravou esta no produto: ela passa a ser o ponto de volta.
    fotoAoAbrirModalRef.current = urlNova;
    descartarFotosNaoSalvas(urlNova);
    await talvezAplicarEmTodos(urlAntiga, urlNova);
  }

  // Escolheu reaproveitar a foto de outro produto — usa a MESMA URL (sem upload).
  function usarFotoExistente(fotoUrl: string) {
    up('foto_url', fotoUrl);
    setEscolhendoFoto(false);
    descartarFotosNaoSalvas(fotoUrl);
  }

  function handleClonar() {
    setMenuAberto(false);
    navigate('/produtos/novo', {
      state: {
        // O clone nasce SEM lugar (`locais` nao vai no state): produto novo ainda nao foi posto em
        // prateleira nenhuma. Mesma regra de sempre — antes era `localizacao: ''`.
        clonarDe: { ...form, codigo_interno: '', ean: '', estoque_atual: '', status: 'ativo', postar_catalogo: false },
      },
    });
  }

  // EXCLUIR DE VERDADE (so admin). Diferente de Inativar: apaga a linha do banco.
  // Existe pro cadastro que NASCEU errado — o duplicado que a importacao de XML criou porque o
  // produto ja existia com outro codigo. Inativar deixaria o fantasma na base pra sempre.
  // Le a PREVIA antes de oferecer o botao vermelho: quem tem venda/nota/movimento nao pode ser
  // apagado, e o admin precisa saber O QUE segura antes de decidir, nao depois de tentar.
  async function handleExcluirDefinitivo() {
    setMenuAberto(false);
    if (!editandoId) return;
    let d: DiagnosticoExclusao;
    try {
      d = await api.get<DiagnosticoExclusao>(`/api/produtos/${editandoId}/pode-excluir`);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Nao consegui conferir se este produto pode ser excluido.');
      return;
    }

    if (!d.pode_excluir) {
      await dialog.alerta(
        `Este produto tem historico e nao pode ser excluido:\n\n`
        + d.impedimentos.map((i) => `  • ${i.qtd} ${i.rotulo}`).join('\n')
        + `\n\nPra tirar da circulacao sem apagar o passado, use Inativo no campo STATUS.`,
        { titulo: 'Nao da pra excluir', tipo: 'aviso' },
      );
      return;
    }

    const junto = Object.entries(d.cai_junto);
    const ok = await dialog.confirmar(
      `Excluir DEFINITIVAMENTE o produto ${d.produto.codigo_interno ?? ''} — ${d.produto.descricao}?\n\n`
      + `Ele nao tem venda, orcamento, nota nem movimento de estoque, entao nada do passado se perde.`
      + (junto.length
        ? `\n\nSome junto: ${junto.map(([t, n]) => `${n} ${ROTULO_CAI_JUNTO[t] ?? t}`).join(', ')}.`
        : '')
      + `\n\nIsso nao tem botao de desfazer na tela.`,
      { titulo: 'Excluir produto', textoConfirmar: 'Excluir', perigoso: true, imagem: d.produto.foto_url ?? undefined },
    );
    if (!ok) return;

    try {
      await api.delete(`/api/produtos/${editandoId}/definitivo`);
      toast.sucesso(`Produto ${d.produto.codigo_interno ?? ''} excluido.`);
      navigate('/produtos');
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao excluir o produto.');
    }
  }

  // Retorna true se gravou com sucesso — o botao Salvar do modal de foto usa
  // isso pra so fechar o modal quando a gravacao deu certo.
  async function handleSubmit(e?: React.FormEvent): Promise<boolean> {
    e?.preventDefault();
    // Outra pessoa esta com a ficha — o botao ja esta apagado; isto pega o Enter num campo.
    if (somenteLeitura) return false;
    // O servidor recusou pela caneta (aba que perdeu sem perceber): a tela passa a somente leitura.
    const recusadoPelaCaneta = (err: unknown) => {
      if (err instanceof ApiError && err.status === 409 && err.corpo?.codigo === 'edicao_de_outro') {
        void perderCaneta(err.corpo.por_nome || 'outro usuario');
        return true;
      }
      return false;
    };
    // Vendedora/caixa: so as Outras Descricoes, pela porta propria (o `PUT` daria 403).
    if (soOutrasDescricoes && editandoId) {
      setSalvando(true);
      try {
        const r = await api.patch<{ outras_descricoes: string | null }>(
          `/api/produtos/${editandoId}/outras-descricoes`,
          { outras_descricoes: form.outras_descricoes },
        );
        const gravado = r.outras_descricoes ?? '';
        setForm((f) => ({ ...f, outras_descricoes: gravado }));
        outrasOriginal.current = gravado;
        setHistorico(null);
        toast.sucesso('Outras descricoes salvas');
        return true;
      } catch (err: any) {
        if (!recusadoPelaCaneta(err)) toast.erro(err.message);
        return false;
      } finally {
        setSalvando(false);
      }
    }
    if (!form.descricao.trim()) {
      toast.erro('descricao obrigatoria');
      return false;
    }
    // A TRAVA da DA-324: com 2+ lugares e nenhuma principal, NAO salva. O backend recusa igual —
    // aqui e so pra o operador ver o erro sem esperar o round-trip.
    if (locais.length >= 2 && !locais.some((l) => l.principal)) {
      toast.erro('Marque qual localizacao e a PRINCIPAL — e dela que a venda baixa.');
      return false;
    }
    // COERENCIA total x prateleiras ([DA-339]). O backend recusa igual; aqui e modal porque o
    // operador precisa VER os dois numeros pra decidir qual dos dois esta errado — um toast some
    // antes de ele conseguir comparar.
    //
    // So confere quando TODAS as prateleiras tem contagem: com uma `—` no meio a soma nao e menor,
    // e desconhecida — travar ali impediria de salvar produto no meio da contagem.
    const todasContadas = locais.length > 0
      && locais.every((l) => l.quantidade !== undefined && l.quantidade !== null && String(l.quantidade) !== '');
    if (todasContadas) {
      const soma = Number(locais.reduce((s, l) => s + Number(l.quantidade ?? 0), 0).toFixed(3));
      const total = Number(Number(form.estoque_atual === '' ? 0 : form.estoque_atual).toFixed(3));
      if (Math.abs(soma - total) > 0.001) {
        await dialog.alerta(
          `O Est. Atual (Geral) esta ${String(total).replace('.', ',')} e a soma das prateleiras da ` +
          `${String(soma).replace('.', ',')} — diferenca de ${String(Number((total - soma).toFixed(3))).replace('.', ',')}.\n\n` +
          'Acerte o total ou as quantidades das prateleiras antes de salvar.',
          { titulo: 'Estoque incoerente', tipo: 'aviso' },
        );
        return false;
      }
    }
    setSalvando(true);
    let fotoGravandoEraNaoSalva = false;
    try {
      const payload: Record<string, any> = { ...form };
      for (const k of CAMPOS_NUM) payload[k] = payload[k] === '' ? 0 : Number(payload[k]);
      const qn = parseQtd(payload.qtd_embalagem_padrao);
      payload.qtd_embalagem_padrao = Number.isFinite(qn) && qn > 0 ? qn : null;
      for (const k of ['grupo_id', 'categoria_id', 'marca_id', 'unidade_venda_id', 'situacao_tributaria_id'] as const) {
        payload[k] = payload[k] === '' ? null : Number(payload[k]);
      }
      payload.fator_conversao = payload.fator_conversao === '' ? null : Number(payload.fator_conversao);
      // So o que o backend precisa — `codigo`/`descricao` do lugar sao enfeite de exibicao.
      payload.localizacoes = locais.map((l) => ({
        localizacao_id: l.localizacao_id,
        principal: l.principal,
        quantidade: l.quantidade,
        quantidade_minima: l.quantidade_minima,
      }));
      payload.marcas_compra = marcasCompra;
      // A foto que vai gravada sai da lista de nao salvas ANTES da gravacao: o produto novo navega
      // pra outra rota logo abaixo, e a limpeza da desmontagem nao pode alcanca-la.
      fotoGravandoEraNaoSalva = fotosNaoSalvasRef.current.delete(form.foto_url);

      // A foto provisoria vira foto do acervo no servidor, com outro endereco: o que foi gravado
      // e' o que a ficha passa a mostrar (e o que o "nada mudou" compara dali pra frente).
      let gravado = form;
      if (editandoId) {
        const salvo = await api.put<{ foto_url: string | null }>(`/api/produtos/${editandoId}`, payload);
        if ((salvo.foto_url ?? '') !== form.foto_url) {
          gravado = { ...form, foto_url: salvo.foto_url ?? '' };
          setForm(gravado);
        }
        formOriginal.current = estadoComparavel(gravado, locais, marcasCompra);
        outrasOriginal.current = form.outras_descricoes;
        setHistorico(null); // forca recarga proxima vez que abrir aba Logs
        setMovimentacoes(null); // idem: o Salvar que mexe no total grava `ajuste_manual`
        toast.sucesso('produto salvo');
      } else {
        const criado = await api.post<{ id: number }>('/api/produtos', payload);
        toast.sucesso('produto cadastrado');
        navigate(`/produtos/${criado.id}`, { replace: true });
      }
      fotoAoAbrirModalRef.current = gravado.foto_url;
      descartarFotosNaoSalvas(form.foto_url);
      // A provisoria ja foi copiada pro acervo — o que sobrou dela na pasta provisoria sai agora.
      if (ehProvisoria(form.foto_url) && gravado.foto_url !== form.foto_url) {
        api.post('/api/upload/foto-ficha/descartar', { url: form.foto_url }).catch(() => {});
      }
      return true;
    } catch (e: any) {
      // Nao gravou: a foto volta a ser "nao salva", senao fechar a tela da foto a deixaria solta.
      if (fotoGravandoEraNaoSalva) fotosNaoSalvasRef.current.add(form.foto_url);
      if (!recusadoPelaCaneta(e)) toast.erro(e.message);
      return false;
    } finally {
      setSalvando(false);
    }
  }

  // Salvar a partir do modal de foto: grava o produto inteiro e, se der certo,
  // fecha o modal — evita ter que sair do modal pra clicar no Salvar do topo.
  async function salvarPeloModalFoto() {
    const ok = await handleSubmit();
    if (ok) setModalFotoAberto(false);
  }

  if (carregando) return <p className="muted">Carregando...</p>;

  const sair = () => { if (window.history.length > 1) navigate(-1); else navigate('/produtos'); };

  // Saida pela seta ← e pelo Cancelar. Confirma quando ha edicao pendente — antes disso
  // a ficha fechava calada e o que estava digitado sumia sem aviso nenhum.
  const fechar = async () => {
    if (isEditing && formAlterado) {
      const ok = await dialog.confirmar(
        'Voce mexeu nesta ficha e ainda nao salvou.\n\nSair agora descarta o que foi alterado.',
        { titulo: 'Sair sem salvar?', textoConfirmar: 'Sair e descartar', textoCancelar: 'Continuar editando', perigoso: true },
      );
      if (!ok) return;
      descartandoRef.current = true;
    }
    sair();
  };

  return (
    <>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => void fechar()}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            <Package size={20} />
            {isEditing ? 'Editar Produto' : 'Novo Produto'}
            {isEditing && (
              <span style={{ position: 'relative' }} ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuAberto((v) => !v)}
                  className="btn-icon"
                  title="Mais opcoes"
                  style={{ marginLeft: 4 }}
                >
                  <MoreVertical size={18} />
                </button>
                {menuAberto && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: '100%',
                    marginTop: 4,
                    width: 180,
                    border: '1px solid var(--popup-border, #e5e7eb)',
                    borderRadius: 6,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    zIndex: 50,
                    background: 'var(--popup-bg, #fff)',
                    color: 'var(--popup-fg, #1f2937)',
                  }}>
                    <button
                      type="button"
                      onClick={handleClonar}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 rounded transition"
                      style={{ background: 'transparent', border: 'none', textAlign: 'left' }}
                    >
                      <Copy size={15} /> Clonar registro
                    </button>
                    {/* Excluir de VERDADE — so admin. Inativar (campo STATUS) segue sendo o caminho
                        normal; isto e' pro cadastro que nasceu errado e nao tem passado. */}
                    {ehAdmin && !somenteLeitura && (
                      <button
                        type="button"
                        onClick={handleExcluirDefinitivo}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 rounded transition"
                        style={{ background: 'transparent', border: 'none', textAlign: 'left', color: '#dc2626' }}
                      >
                        <Trash2 size={15} /> Excluir definitivamente
                      </button>
                    )}
                  </div>
                )}
              </span>
            )}
          </h2>
          <div className="pagina-acoes">
            {/* Botao apagado DIZ por que (convencao da casa). O Salvar so escurece quando
                nao ha o que salvar, e sem esta linha o operador le "travou" — foi metade
                do incidente do NCM em 02/09/2026. */}
            {somenteLeitura ? (
              <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                {edicaoDeOutro} esta editando — somente leitura
              </span>
            ) : !salvando && !formAlterado && (
              <span style={{ alignSelf: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                Nenhuma alteracao pra salvar
              </span>
            )}
            <button type="button" className="btn-secundario" onClick={() => void fechar()} disabled={salvando}>Cancelar</button>
            <button
              type="submit"
              form="produto-form"
              disabled={salvando || !formAlterado || somenteLeitura}
              title={somenteLeitura ? `${edicaoDeOutro} esta editando este produto` : !formAlterado ? 'Nenhuma alteracao pra salvar' : 'Salva o produto'}
              className="btn-primario"
            >
              <Save size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} /> {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>

      <form id="produto-form" onSubmit={handleSubmit} className="space-y-2 flex-1 min-h-0 flex flex-col">
        {/* Topo: Identificacao + Foto lado a lado */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2 mb-1" style={{ fontFamily: 'var(--font-display)' }}>
              <Package size={16} /> Identificacao
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              {soOutrasDescricoes && (
                <div className="mb-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                  Seu perfil altera so as Outras Descricoes desta ficha.
                </div>
              )}
              <Trava ativa={soOutrasDescricoes || somenteLeitura}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-2">
                <div>
                  <div className="flex items-start gap-3">
                    {isEditing && (
                      <div className="flex gap-3 pt-1">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Status</span>
                          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap">
                            <input type="radio" name="statusProduto" checked={form.status === 'ativo'} onChange={() => up('status', 'ativo')} />
                            Ativo
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap">
                            <input type="radio" name="statusProduto" checked={form.status !== 'ativo'} onChange={() => up('status', 'inativo')} />
                            Inativo
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Codigo Interno</label>
                      <input
                        type="text"
                        name="codigo_interno"
                        value={form.codigo_interno}
                        onChange={handleChange}
                        placeholder="Auto"
                        className={`cel-num w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${form.status === 'ativo' ? 'text-blue-600' : 'text-red-400 line-through'}`}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
                  <input type="text" name="referencia" value={form.referencia} onChange={handleChange} maxLength={20} placeholder="Cod. fornecedor"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <LocalizacoesProduto valor={locais} onChange={aplicarLocais} minimoHerdado={form.estoque_minimo} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">EAN (Codigo de Barras)</label>
                  {/* So digitos (05/10/2026): ponto/letra no EAN derruba a nota com cStat 225. O
                      filtro tira o que nao e numero ao digitar e ao colar; o servidor recusa igual.
                      O aviso abaixo (tamanho/digito verificador) NAO trava o Salvar — e o mesmo
                      motivo que a emissao vai recusar, dito antes da venda. */}
                  <input type="text" name="ean" inputMode="numeric" value={form.ean}
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setForm((prev) => ({ ...prev, ean: v })); }}
                    placeholder="Somente numeros (vazio = SEM GTIN na nota)"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  {motivoEanInvalido(form.ean) && (
                    <div style={{ marginTop: 3, padding: '2px 6px', borderRadius: 4, background: '#fde68a', color: '#000', fontSize: 11 }}>
                      A nota fiscal vai recusar: {motivoEanInvalido(form.ean)}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descricao <span className="text-red-500">*</span>
                  </label>
                  {/* 14px em pixel (pedido dele, 04/11/2026): o `text-sm` saia 12,25px — a base da casa e' 14px, nao 16. */}
                  <UpperInput value={form.descricao} onChange={(v) => up('descricao', v)} required placeholder="Ex: Fio Flexivel 2,5mm Azul"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-[14px] font-bold focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descricao Complementar</label>
                  <UpperInput value={form.descricao_complementar} onChange={(v) => up('descricao_complementar', v)} placeholder="Detalhes: cor, norma, aplicacao..."
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
              </div>
              </Trava>

              {/* Os nomes que o CLIENTE usa (pedido do Ruan, 02/10/2026): a busca de produto procura
                  aqui tambem. Fora da Trava de proposito — e' o unico campo que todo perfil grava. */}
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Outras Descricoes <span className="text-gray-400 font-normal">(como o cliente tambem chama — a busca acha por aqui)</span>
                </label>
                <Trava ativa={somenteLeitura}>
                <UpperInput value={form.outras_descricoes} onChange={(v) => up('outras_descricoes', v)}
                  placeholder="Nomes que o cliente usa, separados por virgula"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </Trava>
              </div>

              <Trava ativa={soOutrasDescricoes || somenteLeitura}>

              {/* Linha de classificacao (Grupo/Categoria/Marca + Unidades) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mt-2">
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
                  <SelectComPlus label="Grupo" opcoes={grupos.map((g) => ({ value: g.id, label: g.nome }))}
                    value={form.grupo_id} onChange={(v) => up('grupo_id', v === '' ? '' : Number(v))} onAbrirNovo={() => abrirCadastroRapido('grupo')} />
                  <SelectComPlus label="Categoria" opcoes={categorias.map((c) => ({ value: c.id, label: c.nome }))}
                    value={form.categoria_id} onChange={(v) => up('categoria_id', v === '' ? '' : Number(v))} onAbrirNovo={ehAdmin ? () => abrirCadastroRapido('categoria') : undefined} />
                  <SelectComPlus label="Marca" opcoes={marcas.map((m) => ({ value: m.id, label: m.nome }))}
                    value={form.marca_id} onChange={(v) => up('marca_id', v === '' ? '' : Number(v))} onAbrirNovo={() => abrirCadastroRapido('marca')} />
                  {/* Marcas de COMPRA (DA-386): eixo separado da Marca (exibicao). Busca do MESMO cadastro
                      de marcas; aqui so se ESCOLHE quais o produto tem. Alimenta o filtro de compra. */}
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Tags size={13} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
                      Marcas de compra <span className="text-gray-400 font-normal">(pra filtrar na hora de comprar)</span>
                    </label>
                    <ComboBox
                      opcoes={marcas.filter((m) => !marcasCompra.includes(m.id)).map((m) => ({ value: m.id, label: m.nome }))}
                      value=""
                      onChange={(v) => { if (v !== '') setMarcasCompra((p) => (p.includes(Number(v)) ? p : [...p, Number(v)])); }}
                      placeholder="Adicionar marca..."
                    />
                    {marcasCompra.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {marcasCompra.map((mid) => (
                          <span key={mid} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: 'var(--primary)', color: '#fff' }}>
                            {marcas.find((m) => m.id === mid)?.nome ?? `#${mid}`}
                            <button type="button" title="Remover marca de compra" className="hover:opacity-70"
                              onClick={() => setMarcasCompra((p) => p.filter((x) => x !== mid))}>
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descricao do Fornecedor</label>
                      <div className="w-full px-3 py-1.5 border rounded-lg text-sm text-gray-500 italic break-words"
                        style={{ backgroundColor: 'var(--input-bg, #f9fafb)', borderColor: 'var(--input-border, #d1d5db)' }}
                        title="Como o fornecedor chama este produto no catalogo (somente leitura).">
                        {descFornecedor || '— sem fornecedor no catalogo'}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidade de Venda</label>
                  <div className="flex gap-1 items-center">
                    <ComboBox opcoes={unidadesOfertadas(unidades, form.unidade_venda_id).map((u) => ({ value: u.id, label: rotuloUnidade(u) }))}
                      value={form.unidade_venda_id} onChange={(v) => up('unidade_venda_id', v === '' ? '' : Number(v))} placeholder="Selecione..." />
                    <button type="button" onClick={() => abrirCadastroRapido('unidade')}
                      className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0" title="Nova unidade">
                      <Plus size={16} />
                    </button>
                    <input type="checkbox" checked={form.permite_fracionado}
                      onChange={(e) => up('permite_fracionado', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 ml-1"
                      title="Permite venda fracionada" />
                  </div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                    Unidade de Compra <span className="text-gray-400 font-normal">(+ Qtd na Emb + UN do conteudo)</span>
                  </label>
                  <div className="flex gap-1">
                    <ComboBox opcoes={unidadesOfertadas(unidades, form.unidade_compra).map((u) => ({ value: u.sigla, label: rotuloUnidade(u) }))}
                      value={form.unidade_compra} onChange={(v) => up('unidade_compra', String(v))} placeholder="Selecione..." />
                    <button type="button" onClick={() => abrirCadastroRapido('unidade_compra')}
                      className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0" title="Nova unidade">
                      <Plus size={16} />
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="qtd_embalagem_padrao"
                      value={form.qtd_embalagem_padrao}
                      onChange={(e) => up('qtd_embalagem_padrao', e.target.value)}
                      onBlur={(e) => {
                        const n = parseQtd(e.target.value);
                        up('qtd_embalagem_padrao', Number.isFinite(n) && n > 0 ? formatQtd(n) : '');
                      }}
                      placeholder="Qtd"
                      className="cel-num w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-shrink-0"
                      title="Quantidade por embalagem (ex: 1600 metros por bobina)."
                    />
                    <select
                      value={form.unidade_conteudo_embalagem}
                      onChange={(e) => up('unidade_conteudo_embalagem', e.target.value)}
                      className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-shrink-0 bg-white"
                      title="Unidade do conteudo da embalagem (ex: BB com 1600 M)."
                    >
                      <option value="">UN…</option>
                      {unidadesOfertadas(unidades, form.unidade_conteudo_embalagem).map((u) => <option key={u.id} value={u.sigla}>{u.sigla}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* A calculadora de custo escreve em `preco_custo`, que e' um dos campos so-admin
                  ([DA-595]) — pro estoquista ela era um botao que so levava a um 403 no Salvar.
                  Sai pra quem nao pode usa-la ([DA-602]). */}
              {!valorTravado && (
                <div className="mt-2">
                  <BotaoEstimativaCusto onUsar={(c) => up('preco_custo', String(c))} />
                </div>
              )}

              {/* ⚠️ O aviso amarelo "Valor e estoque so o administrador altera" SAIU aqui ([DA-602]).
                  Ele nasceu com a [DA-595] e passou a MENTIR quando a [DA-597] abriu o ajuste pro
                  JOAO: ele move o estoque 1.544 vezes por mes pela propria ficha, pelos campos de
                  prateleira do bloco Identificacao. O que substitui o aviso nao e' silencio — e' a
                  PORTA: o `Est. Atual` virou botao que abre o `ModalAjustarEstoque`. Os precos
                  seguem travados e continuam dizendo o porque pelo `title`. */}

              {/* Linha densa: Custo | Pix | Mrg Pix | Cartao | Mrg Cartao | Est Atual | Est Min | Fator Conv
                  ⚠️ As 8 colunas deixaram de ser IGUAIS ([DA-602], pedido dele: *"diminua 20% na
                  largura destes campos que o botao vai caber com folga"*). Os 5 primeiros cedem 20%
                  (149 -> 119px medidos no viewport dele) pro `Est. Atual` dividir a coluna com o
                  botao Ajustar. **As frações sao MEDIDAS, nao chutadas**: com 119px o campo de preco
                  ainda comporta `1234,5678`, e o maior preco do acervo e' `497,8779`; o `Est. Atual`
                  fica com 132px, que aguenta ate `999988,5` — o maior estoque real e' `4213`.
                  ⚠️ **O botao Ajustar tem COLUNA PROPRIA, e e' isso que deixa os 8 campos iguais**
                  ([DA-602], pedido dele: *"ajuste para todos os campos ficarem com o mesmo tamanho"*).
                  Enquanto ele dividia a celula do Est. Atual, aquele campo era sempre o menor da
                  linha — e compensar alargando a coluna deixava o Est. Atual maior que os vizinhos.
                  Com 9 colunas (8 iguais + 78px pro botao) todos os campos medem **138px** no
                  viewport dele; medido, nao estimado.
                  ⚠️ **`minmax(0,1fr)`, nunca `1fr` puro**: `1fr` e' `minmax(auto,1fr)` e o `auto`
                  impede a coluna de encolher abaixo do conteudo — com `fr` puro a coluna do Est.
                  Atual travava no tamanho do campo+botao e comia a largura das outras.
                  So no `lg`: no `md` a grade vira 4 colunas e o botao ocupa uma celula inteira,
                  alinhado com os campos (conferido em 768px). */}
              <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-x-2 gap-y-2 mt-2${isEditing ? ' lg:[grid-template-columns:repeat(6,minmax(0,1fr))_78px_repeat(2,minmax(0,1fr))]' : ''}`}>
                <CampoLabel label="Custo (R$)">
                  <input type="number" step="0.0001" name="preco_custo" value={form.preco_custo} onChange={handleChange}
                    readOnly={valorTravado} title={valorTravado ? TITULO_TRAVADO : undefined}
                    className={`cel-num input-preco-custo w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500${valorTravado ? ' bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`} />
                </CampoLabel>
                <CampoLabel label="Pix (R$)">
                  <input type="number" step="0.0001" name="preco_pix" value={form.preco_pix} onChange={handleChange}
                    readOnly={valorTravado} title={valorTravado ? TITULO_TRAVADO : undefined}
                    className={`cel-num input-preco-pix w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500${valorTravado ? ' bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`} />
                </CampoLabel>
                <CampoLabel label="Mrg Pix">
                  <div className="cel-num px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700"
                    style={pixAbaixoDaMeta ? { fontWeight: 700 } : undefined}
                    title={metaPix !== null ? `Meta: ${metaPix.toLocaleString('pt-BR')}% de markup${pixAbaixoDaMeta ? ' — ABAIXO da meta' : ''}` : undefined}>
                    {pixAbaixoDaMeta ? `▼ ${margemPix()}` : margemPix()}
                  </div>
                </CampoLabel>
                <CampoLabel label="Cartao (R$)">
                  <div className="cel-num px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700">
                    {precoCartaoCalc.toFixed(4).replace('.', ',')}
                  </div>
                </CampoLabel>
                <CampoLabel label="Mrg Cartao">
                  <div className="cel-num px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700">{margemCartao()}</div>
                </CampoLabel>
                {fracPai ? (
                  <CampoLabel
                    label={isEditing ? 'Est. Atual' : 'Est. Inicial'}
                    title={`Produto filho de fracionamento: o estoque vem do pai (${fracPai.pai_codigo || fracPai.pai_id} ${fracPai.pai_descricao}) x fator ${fracPai.fator}. Para ajustar, fracione do pai na aba Fracionamento.`}
                  >
                    <div className="cel-num w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700">
                      <EstoqueEfetivo
                        proprio={Number(form.estoque_atual || 0)}
                        efetivo={estoqueEfetivo != null ? Number(estoqueEfetivo) : null}
                      />
                    </div>
                  </CampoLabel>
                ) : (
                  <CampoLabel label={isEditing ? 'Est. Atual' : 'Est. Inicial'}>
                    {/* O campo divide a coluna com o botao Ajustar ([DA-602]). O botao vale pros
                        DOIS perfis — o admin pediu a mesma tela que o JOAO (a que pede comentario e
                        mostra as prateleiras), e sem ele o admin so tinha o campo cru.
                        ⚠️ Pro estoquista o campo tambem continua clicavel: ele e' readOnly, e campo
                        que nao aceita digitacao E' botao. Pro admin o campo segue digitavel — nao
                        se tira uma liberdade que ele ja tinha pra empurrar a porta nova. */}
                    <QuantidadeInput name="estoque_atual" valor={form.estoque_atual} onChangeValor={mudarEstoqueAtual}
                      readOnly={valorTravado}
                      onClick={valorTravado ? () => setAjustandoEstoque(true) : undefined}
                      title={valorTravado ? 'Clique para ajustar o estoque — o ajuste pede um comentario e avisa o Ruan.' : undefined}
                      className={`cel-num input-est-atual w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500${isEditing ? ' font-bold' : ''}`}
                      style={isEditing
                        ? (valorTravado
                            ? { '--input-bg': '#fff', '--input-fg': 'var(--num-destaque)', cursor: 'pointer' } as React.CSSProperties
                            : { '--input-bg': '#fde68a', '--input-fg': '#000' } as React.CSSProperties)
                        : undefined} />
                  </CampoLabel>
                )}

                {/* Item PROPRIO da grade, na coluna de 78px — nao filho do CampoLabel. E' o que
                    mantem os 8 campos com a MESMA largura. `alignSelf: end` alinha o botao com os
                    inputs, que tem rotulo em cima. */}
                {isEditing && (
                  <button type="button" onClick={async () => {
                    // A recusa nomeia a saida e ela esta ao alcance: os dois botoes citados
                    // estao no cabecalho desta mesma tela ([DA-560]).
                    if (estoqueAlteradoNaFicha) {
                      await dialog.alerta('Voce mexeu no estoque ou nas prateleiras aqui na ficha e ainda nao salvou.\n\nSalve (ou Cancelar, no topo da tela) antes de usar o Ajustar — senao ele abriria com o numero do banco e sobrescreveria o que voce digitou.');
                      return;
                    }
                    setAjustandoEstoque(true);
                  }}
                    title="Ajustar estoque — abre a tela com as prateleiras, pede o comentario e registra quem mexeu"
                    style={{
                      alignSelf: 'end', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      gap: 5, whiteSpace: 'nowrap', width: '100%', height: 30, padding: '0 9px',
                      border: 0, borderRadius: 8, background: 'var(--primary)', color: '#fff',
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}>
                    <Pencil size={13} style={{ flex: '0 0 auto' }} />
                    Ajustar
                  </button>
                )}
                {/* "Minimo de Compra", nao "Est. Minimo": desde que cada localizacao tem minimo
                    proprio, os dois nomes iguais colavam num borrao so DOIS sinais diferentes —
                    aqui a pergunta e "quando comprar do fornecedor?" (custa dinheiro, demora dias);
                    na prateleira e "quando buscar no deposito?" (custa uma caminhada). Separar os
                    dois e o motivo da localizacao ter estoque proprio (DA-324). */}
                {fracPai ? (
                  <CampoLabel label="Minimo de Compra" title="So vale pro produto pai — e ele que entra na lista de compra. No filho fica desabilitado.">
                    <div className="cel-num w-full px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-400">
                      {form.estoque_minimo && Number(form.estoque_minimo) > 0
                        ? Number(form.estoque_minimo).toLocaleString('pt-BR', { maximumFractionDigits: 3 })
                        : '—'}
                    </div>
                  </CampoLabel>
                ) : (
                  <CampoLabel label="Minimo de Compra" title="Abaixo disso o produto entra na Lista de Compras. Nao confundir com o Est. Minimo de cada localizacao, que manda repor de um lugar pro outro.">
                    <input type="number" step="0.001" name="estoque_minimo" value={form.estoque_minimo} onChange={handleChange}
                      className="cel-num w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  </CampoLabel>
                )}
                <CampoLabel label="Fator Conv." title="Fator de conversao da unidade de compra para venda. Ex: 1 KG = 7,14 m">
                  <input type="number" step="0.0001" name="fator_conversao" value={form.fator_conversao} onChange={handleChange} placeholder="Ex: 7.14"
                    className="cel-num w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </CampoLabel>
              </div>

              {/* Ultima compra (DA-368) — historico so-leitura, discreto: nao e coluna de estoque
                  (nao muda calculo nenhum), so registra quem/quando foi a ultima vez que o
                  comprador fechou compra deste produto (marcado na cotacao ou creditado pela nota). */}
              {ultimaCompra && (
                <div className="mt-1 text-xs text-gray-400">
                  Última compra: {formatDataSP(ultimaCompra.em)}
                  {ultimaCompra.fornecedor ? ` · ${ultimaCompra.fornecedor}` : ''}
                </div>
              )}
              </Trava>
            </div>
          </div>

          {/* Foto lateral */}
          <div>
            <h2 aria-hidden="true" className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2 mb-1 invisible">
              <ImageIcon size={16} /> Foto
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-5 lg:w-56">
              <div className="flex flex-col items-center gap-2">
                <div
                  // Somente leitura (outra pessoa na ficha): a foto so se ve — o modal dela troca,
                  // remove e salva o produto, e soltar/colar imagem tambem grava.
                  onClick={() => { if (!somenteLeitura) abrirModalFoto(); }}
                  onKeyDown={(e) => { if (!somenteLeitura && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); abrirModalFoto(); } }}
                  onDrop={somenteLeitura ? undefined : handleDrop} onDragOver={somenteLeitura ? undefined : handleDragOver}
                  onDragLeave={somenteLeitura ? undefined : handleDragLeave}
                  tabIndex={0}
                  role="button"
                  title="Clique para gerenciar a foto"
                  // Fundo BRANCO quando ha foto: a derivada sai sempre achatada em
                  // branco pelo servidor, entao com object-contain a faixa vazia
                  // encostava no branco da foto e a emenda lia como "o fundo ficou
                  // em duas cores" (medido: caixa #f3f4f6 contra foto #ffffff).
                  // Sem foto, o cinza continua — ali ele marca a area de soltar.
                  //
                  // ⚠️ INLINE, e sem a classe `bg-white`: o styles.css tem uma regra
                  // `.bg-white.rounded-lg { background: var(--card-bg) }` que promove
                  // qualquer bg-white arredondado a card do tema — no Azul Royal ela
                  // devolvia #cfdef6 no lugar do branco (medido no ar).
                  className={`relative w-40 h-40 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden transition-colors outline-none cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-400 ${
                    dragging ? 'border-blue-500 bg-blue-50' : form.foto_url ? 'border-gray-300' : 'border-gray-300 bg-gray-100'
                  }`}
                  style={form.foto_url && !dragging ? { backgroundColor: '#ffffff' } : undefined}
                >
                  {enviandoFoto ? (
                    <div className="text-center text-blue-500">
                      <Upload size={32} className="mx-auto mb-1 animate-pulse" />
                      <span className="text-xs">Enviando...</span>
                    </div>
                  ) : form.foto_url ? (
                    // object-CONTAIN, nao cover: a moldura e quadrada e o cover corta
                    // pra preencher, entao produto comprido (perfil, cabo, barra)
                    // aparecia como uma faixa diagonal e lia como foto estourada —
                    // com o arquivo inteiro e proporcional o tempo todo (DA-525).
                    <>
                      <img src={form.foto_url} alt="Foto do produto" className="w-full h-full object-contain rounded-lg" />
                      {/* Mesmo selo da Gestao de Fotos (PickerFotoProduto), no mesmo canto. */}
                      {fotoMaster && (
                        <span
                          title="Foto no padrao Master — alta resolucao, quadrada e fundo transparente"
                          style={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            background: 'var(--accent)',
                            color: 'var(--accent-fg)',
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: 0.5,
                            padding: '2px 6px',
                            borderRadius: 999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                        >
                          <BadgeCheck size={10} /> MASTER
                        </span>
                      )}
                    </>
                  ) : dragging ? (
                    <div className="text-center text-blue-500">
                      <Upload size={32} className="mx-auto mb-1" />
                      <span className="text-xs font-medium">Solte aqui</span>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400">
                      <ImageIcon size={32} className="mx-auto mb-1" />
                      <span className="text-xs">Sem foto</span>
                    </div>
                  )}
                </div>
                {/* Nome do ARQUIVO, no mesmo formato do card do acervo (sem pasta nem extensao) —
                    pedido do Ruan, 04/11/2026: duas fotos iguais em arquivos diferentes so se
                    distinguem pelo nome, e o card da foto sem vinculo ja mostra o dele. */}
                {form.foto_url && (
                  <p
                    title={form.foto_url}
                    style={{ color: '#000', fontSize: 12, textAlign: 'center', overflowWrap: 'anywhere', margin: 0 }}
                  >
                    {ehProvisoria(form.foto_url)
                      ? provisoriasRef.current.get(form.foto_url)?.nome ?? ''
                      : nomeDoArquivo(form.foto_url)}
                  </p>
                )}
                <p className="text-xs text-gray-400 text-center">Clique na foto para enviar, trocar ou avaliar.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Abas */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-end gap-1 border-b border-gray-300 relative z-10">
            <BotaoAba ativo={aba === 'observacoes'} onClick={() => setAba('observacoes')} icon={<FileText size={15} />} label="Observacoes" />
            <BotaoAba ativo={aba === 'fiscal'} onClick={() => setAba('fiscal')} icon={<Receipt size={15} />} label="Fiscal" />
            {isEditing && <BotaoAba ativo={aba === 'fracionamento'} onClick={() => setAba('fracionamento')} icon={<Scissors size={15} />} label="Fracionamento" />}
            {isEditing && <BotaoAba ativo={aba === 'fornecedor'} onClick={() => setAba('fornecedor')} icon={<Truck size={15} />} label="Fornecedor de Compra" />}
            {isEditing && <BotaoAba ativo={aba === 'catalogo'} onClick={() => setAba('catalogo')} icon={<Globe size={15} />} label="Catalogo" />}
            {isEditing && <BotaoAba ativo={aba === 'cotacoes'} onClick={() => setAba('cotacoes')} icon={<Tags size={15} />} label="Catalogo Fornecedor" />}
            {isEditing && <BotaoAba ativo={aba === 'logs'} onClick={() => setAba('logs')} icon={<History size={15} />} label="Logs" />}
            {isEditing && <BotaoAba ativo={aba === 'movimentacoes'} onClick={() => setAba('movimentacoes')} icon={<ArrowDownUp size={15} />} label="Movimentacoes" />}
          </div>

          {aba === 'fiscal' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-3 flex-1 min-h-0 overflow-auto">
              <Trava ativa={soOutrasDescricoes || somenteLeitura}>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NCM</label>
                  <AutocompleteNCM value={form.ncm} onChange={(ncm) => up('ncm', ncm)} placeholder="Codigo ou descricao" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEST</label>
                  <input type="text" name="cest" value={form.cest} onChange={handleChange} placeholder="Ex: 1000100"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CFOP</label>
                  <input type="text" name="cfop" value={form.cfop} onChange={handleChange} placeholder="Ex: 5102"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Origem</label>
                  <select name="origem" value={form.origem} onChange={handleChange}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                    <option value="0">0 - Nacional</option>
                    <option value="1">1 - Importacao direta</option>
                    <option value="2">2 - Adquirida mercado interno</option>
                    <option value="3">3 - Nacional c/ 40-70% import.</option>
                    <option value="4">4 - Nacional (basico)</option>
                    <option value="5">5 - Nacional c/ ate 40% import.</option>
                    <option value="6">6 - Importacao s/ similar</option>
                    <option value="7">7 - Importacao c/ similar</option>
                    <option value="8">8 - Nacional c/ +70% import.</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CST/CSOSN</label>
                  <input type="text" name="cst_csosn" value={form.cst_csosn} onChange={handleChange} placeholder="Ex: 102"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Situacao Tributaria</label>
                  <div className="flex gap-1">
                    <ComboBox
                      opcoes={sitTribs.map((st) => ({ value: st.id, label: `${st.descricao}${st.aliquota != null ? ` (${st.aliquota}%)` : ''}` }))}
                      value={form.situacao_tributaria_id}
                      onChange={(v) => up('situacao_tributaria_id', v === '' ? '' : Number(v))}
                      placeholder="Selecione..."
                    />
                    <button type="button" onClick={() => abrirCadastroRapido('situacao_tributaria')}
                      className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0" title="Nova situacao tributaria">
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-x-4 gap-y-1 mt-1">
                <CampoFiscal label="Aliq. ICMS (%)" name="aliquota_icms" value={form.aliquota_icms} onChange={handleChange} />
                <CampoFiscal label="Aliq. IPI (%)" name="aliquota_ipi" value={form.aliquota_ipi} onChange={handleChange} />
                <CampoFiscalText label="CST PIS" name="cst_pis" value={form.cst_pis} onChange={handleChange} placeholder="Ex: 01" />
                <CampoFiscal label="Aliq. PIS (%)" name="aliquota_pis" value={form.aliquota_pis} onChange={handleChange} />
                <CampoFiscalText label="CST COFINS" name="cst_cofins" value={form.cst_cofins} onChange={handleChange} placeholder="Ex: 01" />
                <CampoFiscal label="Aliq. COFINS (%)" name="aliquota_cofins" value={form.aliquota_cofins} onChange={handleChange} />
                <CampoFiscal label="Peso Liquido (kg)" name="peso_liquido" value={form.peso_liquido} onChange={handleChange} step="0.001" />
                <CampoFiscal label="Peso Bruto (kg)" name="peso_bruto" value={form.peso_bruto} onChange={handleChange} step="0.001" />
              </div>
              </Trava>
            </div>
          )}

          {aba === 'observacoes' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-3 flex-1 min-h-0 overflow-auto">
              <Trava ativa={soOutrasDescricoes || somenteLeitura}>
              {/* Uso/Consumo: marca o produto como consumo proprio da loja — some da
                  busca de venda (orcamento/caixa). Estoque e controle seguem normais;
                  nada fiscal muda. Marcavel/desmarcavel a qualquer momento. */}
              <label
                className="flex items-center gap-2 text-sm mb-3 cursor-pointer w-fit px-2 py-1 rounded"
                style={form.uso_consumo ? { backgroundColor: '#fde68a', color: '#000', fontWeight: 700 } : {}}
                title="Item comprado para consumo proprio da loja (ex: inseticida). Nao aparece na busca de produtos do orcamento nem do caixa. O estoque continua sendo controlado normalmente."
              >
                <input type="checkbox" checked={form.uso_consumo} onChange={(e) => up('uso_consumo', e.target.checked)} />
                Item de uso e consumo (nao aparece na venda)
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observacoes do produto</label>
              <p className="text-xs text-gray-500 mb-2">Anotacoes internas livres — fornecedor preferido, comportamento de venda, observacoes de estoque, etc. Nao aparece em NF nem no catalogo publico.</p>
              <textarea
                name="observacoes_produto"
                value={form.observacoes_produto}
                onChange={handleChange}
                placeholder="Escreva aqui qualquer observacao sobre este produto..."
                rows={14}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              />
              </Trava>
            </div>
          )}

          {isEditing && aba === 'fracionamento' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-3 flex-1 min-h-0 overflow-auto">
              <Trava ativa={somenteLeitura}><SecaoFracionamentos produtoId={editandoId!} /></Trava>
            </div>
          )}

          {isEditing && aba === 'fornecedor' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-5 flex-1 min-h-0 overflow-auto">
              {compras.length === 0 ? (
                <div className="text-sm text-gray-500 italic">Nenhuma compra registrada para este produto.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300 text-left text-xs uppercase text-gray-600">
                        <th className="py-2 pr-4 font-semibold">Compra</th>
                        <th className="py-2 pr-4 font-semibold">Fornecedor / Fantasia</th>
                        <th className="py-2 pr-4 font-semibold">Cod. Fornecedor</th>
                        <th className="py-2 pr-4 font-semibold">Descricao do Fornecedor</th>
                        <th className="py-2 pr-4 font-semibold text-right">Valor Unitario</th>
                        <th className="py-2 pr-4 font-semibold">CPF/CNPJ</th>
                        <th className="py-2 pr-4 font-semibold">Chave de Acesso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compras.map((c, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 pr-4">{c.data_emissao ? new Date(c.data_emissao).toLocaleDateString('pt-BR') : '—'}</td>
                          <td className="py-2 pr-4">
                            {c.fornecedor_id ? `${String(c.fornecedor_id).padStart(2, '0')} - ` : ''}
                            {c.fornecedor_nome || '—'}
                          </td>
                          <td className="py-2 pr-4">{c.codigo_fornecedor || '—'}</td>
                          <td className="py-2 pr-4">{c.descricao || '—'}</td>
                          <td className="cel-num py-2 pr-4 text-right font-medium">
                            {Number(c.valor_unitario || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </td>
                          <td className="py-2 pr-4">{c.cpf_cnpj || '—'}</td>
                          <td className="py-2 pr-4">
                            {c.chave_acesso ? (
                              <span className="inline-block font-mono text-xs px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-700">
                                {c.chave_acesso}
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {isEditing && aba === 'catalogo' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-4 flex-1 min-h-0 overflow-auto">
              <Trava ativa={somenteLeitura}>
              <div className="space-y-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <span className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider" title="Inclui no catalogo publico /catalogo. Requer foto e descricao comercial preenchidas.">Vitrine publica</span>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none whitespace-nowrap">
                    <input type="radio" name="postarCatalogo" disabled={!podeEditarCatalogo} checked={form.postar_catalogo === true} onChange={() => up('postar_catalogo', true)} />
                    Postar
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none whitespace-nowrap">
                    <input type="radio" name="postarCatalogo" disabled={!podeEditarCatalogo} checked={form.postar_catalogo !== true} onChange={() => up('postar_catalogo', false)} />
                    Nao postar
                  </label>
                  <div className="ml-auto flex items-start gap-2">
                    <div className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        onClick={() => gerarCatalogoIA('claude')}
                        disabled={!podeEditarCatalogo || gerandoIA !== null}
                        title="Rascunha a descricao comercial e as vantagens com a nossa IA (Claude), analisando os dados e a foto. Revise antes de salvar."
                        style={{ background: 'var(--primary)' }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles size={15} />
                        {gerandoIA === 'claude' ? 'Gerando...' : 'Gerar via IA'}
                      </button>
                      <MicroUsoIA dados={usoIA?.claude} gratis={false} />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        onClick={() => gerarCatalogoIA('gemini')}
                        disabled={!podeEditarCatalogo || gerandoIA !== null}
                        title="Rascunha a descricao comercial e as vantagens com a IA do Google (Gemini). Revise antes de salvar."
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles size={15} />
                        {gerandoIA === 'gemini' ? 'Gerando...' : 'Gerar via Google'}
                      </button>
                      <MicroUsoIA dados={usoIA?.gemini} gratis />
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        onClick={() => gerarCatalogoIA('groq')}
                        disabled={!podeEditarCatalogo || gerandoIA !== null}
                        title="Rascunha a descricao comercial e as vantagens com o Groq (Llama, gratuito). Revise antes de salvar."
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles size={15} />
                        {gerandoIA === 'groq' ? 'Gerando...' : 'Gerar via Groq'}
                      </button>
                      <MicroUsoIA dados={usoIA?.groq} gratis />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descricao Comercial{' '}
                    <span className="text-xs text-gray-400 font-normal">(titulo amigavel curto pro cliente final)</span>
                  </label>
                  <textarea
                    name="descricao_comercial"
                    value={form.descricao_comercial}
                    onChange={(e) => up('descricao_comercial', e.target.value)}
                    onBlur={(e) => up('descricao_comercial', capitalizarPalavras(e.target.value))}
                    rows={2}
                    disabled={!podeEditarCatalogo}
                    placeholder="Ex: Fio paralelo 2x1,5mm preto, rolo 100m — ideal pra iluminacao residencial, tomadas e extensoes."
                    className="no-upper w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    style={{ textTransform: 'none' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vantagens{' '}
                    <span className="text-xs text-gray-400 font-normal">(bullets de aplicacao / diferenciais — uma por linha)</span>
                  </label>
                  <textarea
                    name="vantagens"
                    value={form.vantagens}
                    onChange={(e) => up('vantagens', e.target.value)}
                    rows={6}
                    disabled={!podeEditarCatalogo}
                    placeholder={'• Protege chuveiro, ar condicionado e fogao eletrico\n• Compativel com quadro padrao DIN\n• 5 anos de garantia do fabricante\n• Certificacao INMETRO'}
                    className="no-upper w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                    style={{ textTransform: 'none' }}
                  />
                </div>

                {!podeEditarCatalogo && (
                  <p className="text-xs text-gray-500 italic">Somente admin ou gerente edita campos do catalogo publico.</p>
                )}
              </div>
              </Trava>
            </div>
          )}

          {isEditing && aba === 'cotacoes' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-5 flex-1 min-h-0 overflow-auto">
              <Trava ativa={somenteLeitura}><SecaoCotacoesCatalogo produtoId={editandoId!} /></Trava>
            </div>
          )}

          {isEditing && aba === 'logs' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-5 flex-1 min-h-0 overflow-auto">
              {historico === null ? (
                <div className="text-sm text-gray-400 text-center py-6">Carregando...</div>
              ) : historico.length === 0 ? (
                <div className="text-sm text-gray-500 italic">Nenhuma alteracao registrada.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300 text-left text-xs uppercase text-gray-600">
                        <th className="py-2 pr-4 font-semibold w-36">Data</th>
                        <th className="py-2 pr-4 font-semibold w-28">Usuario</th>
                        <th className="py-2 pr-4 font-semibold w-36">Campo</th>
                        <th className="py-2 pr-4 font-semibold">Anterior</th>
                        <th className="py-2 pr-4 font-semibold">Novo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historico.map((h) => (
                        <tr key={h.id} className="border-b border-gray-100">
                          <td className="py-2 pr-4 text-xs text-gray-500">{h.quando}</td>
                          <td className="py-2 pr-4 text-xs font-medium">{h.usuario_nome || '—'}</td>
                          <td className="py-2 pr-4 text-xs text-gray-700">{h.campo}</td>
                          <td className="py-2 pr-4 text-xs text-red-600">{h.valor_anterior || '—'}</td>
                          <td className="py-2 pr-4 text-xs text-green-700 font-medium">{h.valor_novo || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {isEditing && aba === 'movimentacoes' && (
            <div className="bg-white rounded-b-lg rounded-tr-lg border border-gray-300 border-t-0 p-5 flex-1 min-h-0 overflow-auto">
              {/* Ponte pra aba vizinha: as duas respondem perguntas diferentes e quem abre uma
                  costuma estar procurando a outra. Vai no topo porque o estado VAZIO tambem
                  precisa dela — produto sem movimento nenhum e' onde a duvida aparece. */}
              <div className="text-xs text-gray-500 mb-3">
                Tudo que entrou e saiu do estoque deste produto. O que uma pessoa editou no cadastro fica na aba <strong>Logs</strong>.
              </div>
              {movimentacoes === null ? (
                <div className="text-sm text-gray-400 text-center py-6">Carregando...</div>
              ) : movimentacoes.length === 0 ? (
                <div className="text-sm text-gray-500 italic">Nenhuma movimentacao de estoque registrada.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-300 text-left text-xs uppercase text-gray-600">
                        <th className="py-2 pr-4 font-semibold w-36">Data</th>
                        <th className="py-2 pr-4 font-semibold w-28">Usuario</th>
                        <th className="py-2 pr-4 font-semibold w-52">Movimento</th>
                        <th className="py-2 pr-4 font-semibold w-32">Documento</th>
                        <th className="py-2 pr-4 font-semibold w-24 text-right">Qtd</th>
                        <th className="py-2 pr-4 font-semibold w-40">Saldo</th>
                        <th className="py-2 pr-4 font-semibold">Observacao</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimentacoes.map((m) => {
                        const sentido = sentidoMovimentacao(m.estoque_antes, m.estoque_depois);
                        const qtd = Number(m.quantidade);
                        // ⚠️ Sem verde/vermelho: o sinal e a seta carregam a direcao sozinhos
                        // ([[user_daltonismo]]), como o <ModalUltimasAlteracoes> ja faz.
                        const qtdTexto = sentido === 'lateral'
                          ? formatQtd(Math.abs(qtd))
                          : `${sentido === 'entrada' ? '+' : '-'}${formatQtd(Math.abs(qtd))}`;
                        const documento = m.orcamento_numero || m.nota_numero
                          || (m.origem_codigo && m.destino_codigo ? `${m.origem_codigo} → ${m.destino_codigo}` : null);
                        // ⚠️ A observacao gravada termina em "(orc 6739)" — o ID INTERNO —, e a
                        // coluna ao lado mostra o NUMERO do mesmo pedido (ORC-007389). Lado a lado
                        // os dois numeros leem como dois pedidos diferentes ([DA-591]), e essa
                        // divergencia nasceu aqui: a observacao sozinha nao disputava com ninguem.
                        // So corta quando a coluna Documento REALMENTE mostra o numero — senao
                        // apagaria a unica referencia que a linha tem.
                        const observacao = m.orcamento_numero
                          ? (m.observacao || '').replace(/\s*\(orc\s*\d+\)\s*$/i, '')
                          : m.observacao;
                        return (
                          <tr key={m.id} className="border-b border-gray-100">
                            <td className="py-2 pr-4 text-xs text-gray-500">{formatDataHoraSP(m.criado_em)}</td>
                            <td className="py-2 pr-4 text-xs font-medium">{m.usuario_login || '—'}</td>
                            <td className="py-2 pr-4 text-xs text-gray-700">{rotuloMovimentacao(m.tipo)}</td>
                            <td className="py-2 pr-4 text-xs text-gray-700">{documento || '—'}</td>
                            <td className="py-2 pr-4 text-xs text-right font-semibold tabular-nums">{qtdTexto}</td>
                            <td className="py-2 pr-4 text-xs tabular-nums">
                              <span style={{ color: 'var(--text-muted)' }}>{formatQtd(Number(m.estoque_antes ?? 0))}</span>
                              <span style={{ color: 'var(--text-muted)', margin: '0 5px' }}>→</span>
                              <span className="font-semibold">{formatQtd(Number(m.estoque_depois ?? 0))}</span>
                            </td>
                            <td className="py-2 pr-4 text-xs text-gray-500">{observacao || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {movimentacoes.length >= 500 && (
                    <div className="text-xs text-gray-500 mt-3">
                      Mostrando as 500 movimentacoes mais recentes deste produto.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </form>

      {modalRapido && (
        <ModalCadastroRapido
          titulo={modalRapido.titulo}
          endpoint={modalRapido.endpoint}
          campos={modalRapido.campos}
          onSalvo={(novo) => aplicarCadastroRapido(modalRapido.tipo, novo)}
          onFechar={() => setModalRapido(null)}
        />
      )}

      {/* ⚠️ O ajuste GRAVA sozinho (rota propria), entao ao voltar ele tem que reancorar o
          `formOriginal` — senao a ficha continuaria achando que ha alteracao pendente e o Salvar
          reenviaria o estoque VELHO por cima do que acabou de ser gravado. */}
      {ajustandoEstoque && editandoId && (
        <ModalAjustarEstoque
          produtoId={editandoId}
          codigo={form.codigo_interno || String(editandoId)}
          descricao={form.descricao}
          unidade={unidades.find((u) => u.id === form.unidade_venda_id)?.sigla ?? 'UN'}
          estoqueAtual={form.estoque_atual}
          locais={locais}
          onFechar={() => setAjustandoEstoque(false)}
          onSalvo={({ estoque_atual, locais: novos }) => {
            const novoForm = { ...form, estoque_atual };
            setForm(novoForm);
            setLocais(novos);
            formOriginal.current = estadoComparavel(novoForm, novos, marcasCompra);
            setMovimentacoes(null); // o ajuste grava em movimentacoes_estoque ([DA-595])
            setAjustandoEstoque(false);
            toast.mostrar('Estoque ajustado. O Ruan foi avisado.', 'sucesso');
          }}
        />
      )}

      <Modal
        aberto={modalFotoAberto}
        onFechar={fecharModalFotoSemSalvar}
        titulo={
          fotoParaEnquadrar ? 'Ajustar foto'
            : escolhendoFoto ? 'Pesquisar Fotos'
            : 'Foto do produto'
        }
        largura={escolhendoFoto ? 1400 : 780}
        rodape={
          !fotoParaEnquadrar && !escolhendoFoto ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <button
                type="button"
                className="btn-primario"
                onClick={salvarPeloModalFoto}
                disabled={salvando || enviandoFoto || !formAlterado}
                title={!formAlterado ? 'Nenhuma alteracao pra salvar' : 'Salva o produto e fecha'}
              >
                <Save size={14} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          ) : undefined
        }
      >
        {escolhendoFoto ? (
          <PickerFotoProduto
            onEscolher={usarFotoExistente}
            onCancelar={() => setEscolhendoFoto(false)}
            onFotoExcluida={(url) => { if (url === form.foto_url) up('foto_url', ''); }}
          />
        ) : fotoParaEnquadrar ? (
          <EditorEnquadrarFoto
            fotoUrl={fotoParaEnquadrar}
            aplicando={enviandoFoto}
            onCancelar={fecharEnquadramento}
            onAplicar={aplicarFotoEditada}
          />
        ) : (
        <div className="flex flex-col sm:flex-row gap-6 items-start justify-center text-sm">
          {/* Coluna esquerda: foto */}
          <div
            onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
            tabIndex={0}
            // Mesmo motivo do card menor acima, e pelo mesmo caminho (inline, sem
            // a classe bg-white, que o tema promoveria a card).
            className={`w-[400px] h-[400px] max-w-full flex-shrink-0 mx-auto sm:mx-0 border-2 border-dashed rounded-lg flex items-center justify-center overflow-hidden outline-none ${
              dragging ? 'border-blue-500 bg-blue-50' : form.foto_url ? 'border-gray-300' : 'border-gray-300 bg-gray-100'
            }`}
            style={form.foto_url && !dragging ? { backgroundColor: '#ffffff' } : undefined}
          >
            {enviandoFoto ? (
              <div className="text-center text-blue-500">
                <Upload size={44} className="mx-auto mb-1 animate-pulse" />
                <span className="text-sm">Enviando...</span>
              </div>
            ) : form.foto_url ? (
              // object-CONTAIN pelo mesmo motivo do card menor acima (DA-525).
              <img src={form.foto_url} alt="Foto do produto" className="w-full h-full object-contain rounded-lg" />
            ) : dragging ? (
              <div className="text-center text-blue-500">
                <Upload size={44} className="mx-auto mb-1" />
                <span className="text-sm font-medium">Solte aqui</span>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <ImageIcon size={44} className="mx-auto mb-1" />
                <span className="text-sm">Sem foto</span>
              </div>
            )}
          </div>

          {/* Coluna direita: controles */}
          <div className="flex flex-col gap-3 items-center w-56">
            <label className={`flex items-center justify-center gap-2 w-52 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition ${
              enviandoFoto ? 'bg-gray-100 text-gray-400 cursor-wait' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}>
              <Upload size={16} /> {enviandoFoto ? 'Enviando...' : form.foto_url ? 'Trocar foto' : 'Enviar foto'}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUploadFoto} disabled={enviandoFoto} className="hidden" />
            </label>
            <button type="button" onClick={() => setEscolhendoFoto(true)} disabled={enviandoFoto}
              title="Pesquisa no acervo de fotos por nome do produto, codigo ou nome da foto"
              className="flex items-center justify-center gap-2 w-52 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition disabled:opacity-50">
              <Images size={16} /> Pesquisar Fotos
            </button>
            {form.foto_url && (
              <button type="button" onClick={handleRemoverFoto}
                className="flex items-center justify-center gap-2 w-52 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition">
                <Trash2 size={16} /> Remover
              </button>
            )}
            {form.foto_url && (
              // Abre o RECORTE em alta: a foto da moldura e' a versao leve de fundo branco, e um
              // ajuste feito sobre ela nunca passaria na regua do Master.
              <button type="button" onClick={() => setFotoParaEnquadrar(masterDaFoto ?? form.foto_url)}
                title="Arrasta e usa o zoom pra enquadrar o produto no quadro"
                className="flex items-center justify-center gap-2 w-52 px-4 py-2 text-sm font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 rounded-lg transition">
                <Crop size={16} /> Ajustar foto
              </button>
            )}

            {isEditing && (
              <AvaliarFotoCatalogo
                produtoId={editandoId!}
                fotoUrl={form.foto_url || null}
                podeEditar={podeEditarCatalogo}
                onFotoOtimizada={aoOtimizarFoto}
              />
            )}
            <p className="text-sm text-gray-400 text-center">
              So entra foto no padrao <strong>Master</strong>: 2000px no maior lado
              e fundo que o recorte consiga separar. O nome do arquivo vira o nome da foto no acervo.
            </p>
            {avisoSubstituicao && (
              <p style={{ color: '#000', fontSize: 12, textAlign: 'center', margin: 0 }}>{avisoSubstituicao}</p>
            )}
          </div>
        </div>
        )}
      </Modal>

      {erroFoto && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-xl shadow-xl w-full max-w-sm" style={{ backgroundColor: 'var(--panel-bg)', position: 'relative' }}>
            <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={() => setErroFoto('')} aria-label="Fechar">×</button>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-red-600">Erro ao enviar foto</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700">{erroFoto}</p>
            </div>
            <div className="flex justify-end px-5 pb-4">
              <button type="button" onClick={() => setErroFoto('')}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface SelectComPlusProps {
  label: string;
  opcoes: { value: number; label: string }[];
  value: number | '' | null | undefined;
  onChange: (v: string | number | '') => void;
  // Ausente = sem o "+" (cadastro que o perfil logado nao pode criar — Categoria e' so do admin).
  onAbrirNovo?: () => void;
}
function SelectComPlus({ label, opcoes, value, onChange, onAbrirNovo }: SelectComPlusProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-1">
        <ComboBox opcoes={opcoes} value={value ?? ''} onChange={onChange} placeholder="Selecione..." />
        {onAbrirNovo && (
          <button type="button" onClick={onAbrirNovo}
            className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0" title={`Novo ${label.toLowerCase()}`}>
            <Plus size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// Trava um trecho da ficha pra quem so grava as Outras Descricoes. `<fieldset disabled>` desliga
// todo input/select/botao nativo de dentro de uma vez; o estilo zera a moldura que o navegador
// poe no fieldset, pra a ficha nao mudar de desenho.
function Trava({ ativa, children }: { ativa: boolean; children: React.ReactNode }) {
  return (
    <fieldset disabled={ativa} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {children}
    </fieldset>
  );
}

function CampoLabel({ label, title, children }:{ label: string; title?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1 truncate" title={title || label}>{label}</label>
      {children}
    </div>
  );
}

interface CampoFiscalProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  step?: string;
}
function CampoFiscal({ label, name, value, onChange, step = '0.01' }: CampoFiscalProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="number" step={step} name={name} value={value} onChange={onChange}
        className="cel-num w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}
function CampoFiscalText({ label, name, value, onChange, placeholder }: CampoFiscalProps & { placeholder?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" name={name} value={value} onChange={onChange} placeholder={placeholder}
        className="cel-num w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}

function BotaoAba({ ativo, onClick, icon, label }: { ativo: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button type="button" onClick={onClick}
      style={ativo ? { color: 'var(--tabs-active-fg, var(--primary))' } : undefined}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-md border transition ${
        ativo
          ? 'bg-white border-gray-300 border-b-white -mb-px'
          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
      }`}>
      {icon} {label}
    </button>
  );
}
